// API de la refonte — cycle de vie RDV. TOUTE transition passe par la RPC
// advance_appointment (moteur en base) : on n'écrit jamais lifecycle_status en direct.
import { supabase } from './auth';
import { isBillable, billsPerRdv, LIFECYCLE } from './appointmentLifecycle';
import type { LifecycleStatus, ContractType } from './appointmentLifecycle';
import type { AppointmentEvent } from '../components/refonte/LifecycleTimeline';
import { normalizeClientNote } from './clientNote';

export interface RdvRow {
  id: string;
  prospect_name: string | null;
  company_name: string | null;
  job_title: string | null;
  date: string | null;
  time: string | null;
  lifecycle_status: LifecycleStatus | null;
  icp_conformant: boolean | null;
  client_id: string | null;
  sdr_id?: string | null;
  clients?: { name: string | null } | null;
  calibration?: { pricing_model: string | null } | null;
  sdr?: { sales_name: string | null } | null;
}

/** Clé de contrat brute (base : pricing_model ou clients.contract_type) → type UI. null = inconnu. */
export function contractTypeFromRaw(raw?: string | null): ContractType | null {
  if (raw === 'fixed_plus_variable') return 'variable';
  if (raw === 'fixed') return 'fixed';
  if (raw === 'days') return 'days';
  if (raw === 'gtm') return 'gtm';
  return null;
}

/** pricing_model (base) → type de contrat côté UI. null = inconnu (permissif).
 * Le join `calibrations(pricing_model)` peut renvoyer un objet OU un tableau (plusieurs
 * versions de calibrage par client) : on prend le 1er pricing_model non nul. */
export function contractTypeOf(rdv: RdvRow): ContractType | null {
  const cal: any = (rdv as any).calibration;
  const arr = Array.isArray(cal) ? cal : cal ? [cal] : [];
  const pm = arr.map((c: any) => c?.pricing_model).find(Boolean) ?? null;
  return contractTypeFromRaw(pm);
}

/** Fait avancer un RDV d'un état à l'autre via le moteur (valide transition + rôle, écrit le journal). */
export async function advanceAppointment(
  appointmentId: string,
  toStatus: LifecycleStatus,
  opts?: { note?: string; icpConformant?: boolean; reason?: string; noShowReason?: 'prospect' | 'client' }
): Promise<RdvRow> {
  const { data, error } = await supabase.rpc('advance_appointment', {
    p_appointment_id: appointmentId,
    p_to_status: toStatus,
    p_note: opts?.note ?? null,
    p_icp_conformant: opts?.icpConformant ?? null,
    p_reason: opts?.reason ?? null,
    // Granularité du no-show (réunion GTM 10/09) : qui l'a causé — le prospect ou le client.
    // Porté par la RPC (sécu 08/10) : les clients n'ont plus d'UPDATE direct sur appointments.
    p_no_show_reason: (toStatus === 'no_show' ? opts?.noShowReason ?? null : null),
  });
  if (error) throw error;
  return data as RdvRow;
}

/** Journal append-only d'un RDV (preuve horodatée), ordre chronologique. */
export async function getAppointmentEvents(appointmentId: string): Promise<AppointmentEvent[]> {
  const { data, error } = await supabase
    .from('appointment_events')
    .select('id,event_type,from_status,to_status,actor_role,note,created_at')
    .eq('appointment_id', appointmentId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as AppointmentEvent[];
}

// ── Canal / offre commerciale (orthogonal à la formule de prix) ──────────
// Cold call / Mailing / GTM = les 3 offres Hook. Le canal dit "d'où vient
// l'oseille" ; la formule (forfait/hybride/jours) dit "comment elle est facturée".
export type Channel = 'cold_call' | 'mailing' | 'gtm' | 'data' | 'autre';
export const CHANNEL_LABELS: Record<string, string> = {
  cold_call: 'Cold call', mailing: 'Mailing', gtm: 'GTM', data: 'Data', autre: 'Autre',
};
export const CHANNELS: Channel[] = ['cold_call', 'mailing', 'gtm', 'data', 'autre'];
export function channelLabel(c?: string | null): string {
  return (c && CHANNEL_LABELS[c]) || 'Cold call';
}

// ── Multi-contrats par client (principal + Data + upsell…) ─────────────────
// La SOURCE de vérité = les lignes de client_contracts. Chaque ligne porte sa formule
// (Forfait/Hybride/GTM), son canal, son prix/mois, ses jours et — si hybride — son prix/RDV.
// Le « contrat principal » (calibration) est recalculé depuis les lignes → facturation & BP suivent.
export interface ClientContractLine {
  id: string; pricing: ContractType | null; channel: string | null; label: string | null;
  monthly: number; days: number | null; pricePerRdv: number | null; createdAt: string;
  startDate: string | null; endDate: string | null; isActive: boolean;
  // Pour une ligne GTM, `days` = MISSIONS vendues (places), pas des jours.
}
export async function getClientContracts(clientId: string, includeEnded = false): Promise<ClientContractLine[]> {
  let q = supabase.from('client_contracts')
    .select('id,pricing_model,channel,label,monthly_price,days_per_month,price_per_qualified_rdv,created_at,start_date,end_date,is_active')
    .eq('client_id', clientId).order('created_at', { ascending: true });
  if (!includeEnded) q = q.eq('is_active', true);
  const { data } = await q;
  return (data ?? []).map((r: any) => ({
    id: r.id, pricing: contractTypeFromRaw(r.pricing_model), channel: r.channel ?? null, label: r.label ?? null,
    monthly: Number(r.monthly_price) || 0, days: r.days_per_month ?? null,
    pricePerRdv: r.price_per_qualified_rdv != null ? Number(r.price_per_qualified_rdv) : null, createdAt: r.created_at,
    startDate: r.start_date ?? null, endDate: r.end_date ?? null, isActive: !!r.is_active,
  }));
}
/** Pose (ou retire) la date de fin d'une ligne. Fin passée/aujourd'hui → la ligne se désactive
 *  (transition de contrat : l'ancienne ligne se ferme, la nouvelle vit). Fin future = programmée. */
export async function setContractEndDate(lineId: string, endDate: string | null): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);
  const patch: any = { end_date: endDate };
  if (endDate && endDate <= today) patch.is_active = false;
  const { data: row } = await supabase.from('client_contracts').select('client_id').eq('id', lineId).limit(1);
  const { error } = await supabase.from('client_contracts').update(patch).eq('id', lineId);
  if (error) throw error;
  if (row?.[0]?.client_id) { await syncPrimaryContract(row[0].client_id); await syncBpFromContracts(row[0].client_id); }
}
/** Reflète les contrats d'un client dans le BP (bp_import), pour que le BP ne mente plus :
 *  - une ligne BP par CANAL ; si aucune n'existe → création (mois remplis de début à fin de contrat) ;
 *  - si une ligne BP existe déjà (saisie manuelle) → on ne touche JAMAIS son passé, on met seulement
 *    à zéro les mois situés APRÈS la dernière date de fin connue des lignes actives du canal. */
const BP_YEAR = 2026; // bp_import porte 12 mois (m1..m12) de l'année 2026
async function syncBpFromContracts(clientId: string): Promise<void> {
  const [{ data: cl }, { data: lines }, { data: bps }] = await Promise.all([
    supabase.from('clients').select('name').eq('id', clientId).limit(1),
    supabase.from('client_contracts').select('channel,pricing_model,monthly_price,start_date,end_date,is_active').eq('client_id', clientId).eq('is_active', true),
    supabase.from('bp_import').select('name,channel').eq('client_id', clientId),
  ]);
  const name = (cl?.[0] as any)?.name; if (!name) return;
  const nowM = new Date().getMonth() + 1;
  // Fenêtre [mois début, mois fin] d'une ligne dans l'année du BP. Sans début = mois courant ; sans fin = décembre.
  const winOf = (l: any): [number, number] => {
    const sy = l.start_date?.slice(0, 4), ey = l.end_date?.slice(0, 4);
    const sm = !l.start_date ? nowM : sy === String(BP_YEAR) ? Number(l.start_date.slice(5, 7)) : sy < String(BP_YEAR) ? 1 : 13;
    const em = !l.end_date ? 12 : ey === String(BP_YEAR) ? Number(l.end_date.slice(5, 7)) : ey < String(BP_YEAR) ? 0 : 12;
    return [sm, em];
  };
  const byChan = new Map<string, any[]>();
  for (const l of (lines ?? []) as any[]) { const k = l.channel || 'cold_call'; if (!byChan.has(k)) byChan.set(k, []); byChan.get(k)!.push(l); }
  const existingChans = new Set((bps ?? []).map((b: any) => b.channel || 'cold_call'));
  for (const [chan, ls] of byChan) {
    if (existingChans.has(chan)) {
      // Ligne BP déjà là (souvent saisie à la main) : on n'écrase pas — on ferme juste au-delà de la fin.
      const lastEnd = Math.max(...ls.map(l => winOf(l)[1]));
      if (lastEnd < 12) {
        const patch: any = {}; for (let m = lastEnd + 1; m <= 12; m++) patch[`m${m}`] = 0;
        await supabase.from('bp_import').update(patch).eq('client_id', clientId).eq('channel', chan);
      }
    } else {
      const row: any = {
        name: chan === 'cold_call' || chan === 'gtm' ? name : `${name} ${chan.toUpperCase()}`,
        channel: chan, contract_type: ls[0]?.pricing_model ?? null, client_id: clientId,
      };
      for (let m = 1; m <= 12; m++) row[`m${m}`] = ls.reduce((s, l) => { const [sm, em] = winOf(l); return s + (m >= sm && m <= em ? Number(l.monthly_price) || 0 : 0); }, 0);
      await supabase.from('bp_import').insert(row);
    }
  }
}

/** Recalcule le contrat principal (calibration) depuis les lignes actives. */
async function syncPrimaryContract(clientId: string): Promise<void> {
  const { data: lines } = await supabase.from('client_contracts')
    .select('pricing_model,channel,monthly_price,price_per_qualified_rdv').eq('client_id', clientId).eq('is_active', true);
  const ls = (lines ?? []) as any[];
  const hyb = ls.find(l => l.pricing_model === 'fixed_plus_variable');
  const gtm = ls.find(l => l.pricing_model === 'gtm');
  const pricing = hyb ? 'fixed_plus_variable' : gtm ? 'gtm' : 'fixed';
  const monthly = ls.reduce((s, l) => s + (Number(l.monthly_price) || 0), 0);
  // Canal principal par PRIORITÉ (Charles 09/09) : cold call / GTM = contrats principaux,
  // mailing ensuite, data / autre = toujours des bonus. À priorité égale, la plus grosse ligne gagne.
  // Avenria = GTM 5400 + Data 1200 → client GTM (missions, planning, capacité), le data reste une ligne bonus.
  const CH_PRIO: Record<string, number> = { cold_call: 0, gtm: 0, mailing: 1, data: 2, autre: 3 };
  const main = [...ls].sort((a, b) =>
    (CH_PRIO[a.channel] ?? 9) - (CH_PRIO[b.channel] ?? 9) ||
    (Number(b.monthly_price) || 0) - (Number(a.monthly_price) || 0))[0];
  const calFields = { pricing_model: pricing, channel: main?.channel || 'cold_call', fixed_price: monthly, price_per_qualified_rdv: hyb ? (hyb.price_per_qualified_rdv ?? null) : null };
  const { data: exist } = await supabase.from('calibrations').select('id').eq('client_id', clientId).eq('is_active', true).limit(1);
  if (exist?.[0]) await supabase.from('calibrations').update(calFields).eq('id', exist[0].id);
  else await supabase.from('calibrations').insert([{ client_id: clientId, ...calFields, is_active: true, status: 'validated' }]);
  const { data: cal } = await supabase.from('calibrations').select('id').eq('client_id', clientId).eq('is_active', true).limit(1);
  if (cal?.[0]) await supabase.from('appointments').update({ calibration_id: cal[0].id }).eq('client_id', clientId).is('calibration_id', null);
}
/** Ajoute une ligne de contrat (upsell = nouvelle ligne). Ne désactive pas les autres. */
export async function addClientContractLine(clientId: string, input: { pricing: 'fixed' | 'fixed_plus_variable' | 'gtm'; channel: string; label?: string | null; monthly: number; days?: number | null; pricePerRdv?: number | null; startDate?: string | null; endDate?: string | null }): Promise<void> {
  const days = input.days && input.days > 0 ? input.days : null; // GTM : days = missions vendues (places)
  const billing_unit = input.pricing === 'gtm' ? 'subscription' : input.pricing === 'fixed_plus_variable' ? 'package' : days ? 'days' : 'package';
  const { error } = await supabase.from('client_contracts').insert([{
    client_id: clientId, type: input.channel, channel: input.channel, label: input.label || null,
    pricing_model: input.pricing, monthly_price: input.monthly, days_per_month: days,
    price_per_qualified_rdv: input.pricing === 'fixed_plus_variable' ? (input.pricePerRdv ?? null) : null,
    billing_unit, is_active: true,
    start_date: input.startDate || null, end_date: input.endDate || null,
  }]);
  if (error) throw error;
  await syncPrimaryContract(clientId);
  await syncBpFromContracts(clientId); // le BP suit le contrat (création de la ligne si absente)
}
/** Supprime un contrat SAISI PAR ERREUR (fenêtre 7 jours, copie en corbeille côté base —
 *  au-delà la RPC refuse : utiliser la clôture, règle Kevin 06/10). */
export async function deleteContractMistake(id: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc('delete_client_contract_mistake', { p_contract_id: id, p_reason: reason ?? null });
  if (error) throw error;
}
export async function deactivateClientContractLine(id: string): Promise<void> {
  const { data: row } = await supabase.from('client_contracts').select('client_id').eq('id', id).limit(1);
  // Clôture ATOMIQUE (close_client_contract) : end_date posée, et si plus aucun contrat actif →
  // planning futur purgé + client 'termine' + calibration désactivée (le bug Komtop ne peut plus arriver).
  const { error } = await supabase.rpc('close_client_contract', { p_contract_id: id });
  if (error) throw error;
  const cid = (row?.[0] as any)?.client_id;
  if (cid) {
    const { data: rest } = await supabase.from('client_contracts').select('id').eq('client_id', cid).eq('is_active', true).limit(1);
    if (rest?.length) await syncPrimaryContract(cid);  // il reste des lignes → recalcul du contrat principal
    await syncBpFromContracts(cid);
  }
}

// ── Calibrage / contrat client (créer / éditer) ─────────────────────────
export interface CalibrationData {
  channel: Channel | null;
  pricing_model: 'fixed' | 'fixed_plus_variable' | 'gtm' | 'days' | null;
  fixed_price: number | null;
  price_per_qualified_rdv: number | null;
  contestation_window_days: number | null;
  sectors: string[]; zones: string[]; target_functions: string[]; exclusions: string[];
  size_min: number | null; size_max: number | null;
  client_signed_at: string | null;
  version: number | null;
}
export interface ClientCalib {
  id: string; name: string; statut: string | null;
  calibration: CalibrationData | null;
}

const CAL_FIELDS = 'client_id,channel,pricing_model,fixed_price,price_per_qualified_rdv,contestation_window_days,sectors,zones,target_functions,exclusions,size_min,size_max,client_signed_at,version';

/** Tous les clients + leur calibrage actif (null si non défini). */
export async function getClientsForCalibration(): Promise<ClientCalib[]> {
  const [{ data: cls, error: e1 }, { data: cals, error: e2 }] = await Promise.all([
    supabase.from('clients').select('id,name,statut').order('name'),
    supabase.from('calibrations').select(CAL_FIELDS).eq('is_active', true),
  ]);
  if (e1) throw e1; if (e2) throw e2;
  const byClient = new Map<string, any>((cals ?? []).map((c: any) => [c.client_id, c]));
  return (cls ?? []).map((c: any) => {
    const cal = byClient.get(c.id);
    return {
      id: c.id, name: c.name, statut: c.statut,
      calibration: cal ? {
        channel: cal.channel ?? 'cold_call',
        pricing_model: cal.pricing_model, fixed_price: cal.fixed_price,
        price_per_qualified_rdv: cal.price_per_qualified_rdv, contestation_window_days: cal.contestation_window_days,
        sectors: cal.sectors ?? [], zones: cal.zones ?? [], target_functions: cal.target_functions ?? [], exclusions: cal.exclusions ?? [],
        size_min: cal.size_min, size_max: cal.size_max, client_signed_at: cal.client_signed_at, version: cal.version,
      } : null,
    };
  });
}

/** Enregistre le calibrage : désactive l'actif précédent, insère la nouvelle version active. */
export async function saveCalibration(clientId: string, d: CalibrationData, sign: boolean): Promise<void> {
  const { error: e0 } = await supabase.from('calibrations').update({ is_active: false }).eq('client_id', clientId).eq('is_active', true);
  if (e0) throw e0;
  const { error } = await supabase.from('calibrations').insert({
    client_id: clientId, status: 'validated', is_active: true,
    channel: d.channel || 'cold_call',
    pricing_model: d.pricing_model, fixed_price: d.fixed_price,
    price_per_qualified_rdv: d.pricing_model === 'fixed_plus_variable' ? d.price_per_qualified_rdv : null,
    contestation_window_days: d.contestation_window_days ?? 7,
    sectors: d.sectors, zones: d.zones, target_functions: d.target_functions, exclusions: d.exclusions,
    size_min: d.size_min, size_max: d.size_max,
    version: (d.version ?? 0) + 1,
    hook_signed_at: new Date().toISOString(),
    client_signed_at: sign ? new Date().toISOString() : d.client_signed_at,
  });
  if (error) throw error;
}

// Contexte/pitch (client_context) + calibration : tout ce qui a été décidé au calibrage.
export interface ClientContextData {
  icp_summary: string | null; pitch_core: string | null; value_proposition: string | null; tone: string | null;
  objections: any; proof_cases: any; full_calibration: string | null; source_notion_url: string | null;
  validated: boolean | null; generated_at: string | null;
}
export interface ClientCalibrationFull { calibration: CalibrationData | null; context: ClientContextData | null; }
/** Calibrage complet d'un client : ICP + pricing (calibrations) ET pitch/contexte (client_context). */
export async function getClientCalibrationFull(clientId: string): Promise<ClientCalibrationFull> {
  const [{ data: cal }, { data: ctx }] = await Promise.all([
    supabase.from('calibrations').select(CAL_FIELDS).eq('client_id', clientId).eq('is_active', true).limit(1),
    supabase.from('client_context').select('icp_summary,pitch_core,value_proposition,tone,objections,proof_cases,full_calibration,source_notion_url,validated,generated_at')
      .eq('client_id', clientId).order('generated_at', { ascending: false }).limit(1),
  ]);
  const c = cal?.[0] as any;
  return {
    calibration: c ? {
      channel: c.channel ?? 'cold_call', pricing_model: c.pricing_model, fixed_price: c.fixed_price,
      price_per_qualified_rdv: c.price_per_qualified_rdv, contestation_window_days: c.contestation_window_days,
      sectors: c.sectors ?? [], zones: c.zones ?? [], target_functions: c.target_functions ?? [], exclusions: c.exclusions ?? [],
      size_min: c.size_min, size_max: c.size_max, client_signed_at: c.client_signed_at, version: c.version,
    } : null,
    context: (ctx?.[0] as any) ?? null,
  };
}

// ── Business plan (prévisionnel mensuel, importé du BP) ──────────────────
export interface BpRow {
  name: string;
  channel: Channel;
  contractType: ContractType | null;
  months: number[];        // 12 mois (janv→déc) — base BP + variable hybride réelle
  baseMonths: number[];    // 12 mois de bp_import (le forecast ÉDITABLE = le fixe, sans la variable)
  variableMonths: number[]; // 12 mois de variable hybride réelle (RDV validés × prix/RDV)
  pricePerRdv: number | null; // prix du RDV qualifié honoré (hybride), pour afficher "N RDV × prix"
  bpId: string | null;     // id de la ligne bp_import (null si ligne agrégée multi-contrats → éditer les sous-lignes)
  contractMonthly: number | null; // montant mensuel du contrat actif (référence pour le badge d'écart)
  total: number;
  clientId: string | null;
  status: 'actif' | 'inactif' | null;  // depuis clients.statut (null = non matché)
  lines?: BpRow[];         // sous-lignes quand le client a plusieurs contrats (1 ligne client, N contrats)
}
/** Une case de la ventilation canal × formule (ex. "Cold call · Hybride"). */
export interface BpBucket {
  channel: Channel;
  contractType: ContractType | null;
  months: number[];  // 12
  total: number;     // annuel
  count: number;     // nb de lignes/clients
}
export interface BusinessPlan {
  rows: BpRow[];
  monthlyTotals: number[]; // 12
  annualTotal: number;
  activeThisMonth: number; // clients avec CA sur le mois courant
  byChannel: BpBucket[];   // agrégat par canal (Kevin : "combien en mail / en call")
  byBucket: BpBucket[];    // ventilation croisée canal × formule
}

export async function getBusinessPlan(): Promise<BusinessPlan> {
  const [{ data, error }, { data: cls }, { data: cals }, { data: appts }, { data: contracts }] = await Promise.all([
    supabase.from('bp_import').select('*'),
    supabase.from('clients').select('id, statut, name'),
    supabase.from('calibrations').select('client_id, pricing_model, price_per_qualified_rdv').eq('is_active', true),
    supabase.from('appointments').select('client_id, date, lifecycle_status').in('lifecycle_status', ['validated', 'billed']).gte('date', '2026-01-01').lte('date', '2026-12-31'),
    supabase.from('client_contracts').select('client_id, monthly_price, start_date, end_date').eq('is_active', true),
  ]);
  if (error) throw error;
  // Référence contrat = SOMME des contrats actifs qui couvrent le mois courant (cumul multi-contrats).
  const _now = new Date();
  const _first = new Date(_now.getFullYear(), _now.getMonth(), 1).toISOString().slice(0, 10);
  const _last = new Date(_now.getFullYear(), _now.getMonth() + 1, 0).toISOString().slice(0, 10);
  const contractMonthlyById = new Map<string, number>();
  for (const c of (contracts ?? []) as any[]) {
    if (!c.client_id) continue;
    const coversMonth = (!c.start_date || c.start_date <= _last) && (!c.end_date || c.end_date >= _first);
    if (coversMonth) contractMonthlyById.set(c.client_id, (contractMonthlyById.get(c.client_id) || 0) + (Number(c.monthly_price) || 0));
  }
  const statusById = new Map<string, string>((cls ?? []).map((c: any) => [c.id, c.statut]));
  const nameById = new Map<string, string>((cls ?? []).map((c: any) => [c.id, c.name]));
  // Formule dérivée de la calibration du client lié (bp_import.contract_type est quasi vide).
  const pmById = new Map<string, string>();
  const prixById = new Map<string, number>();
  for (const c of (cals ?? []) as any[]) if (c.client_id && c.pricing_model && !pmById.has(c.client_id)) {
    pmById.set(c.client_id, c.pricing_model);
    if (c.price_per_qualified_rdv != null) prixById.set(c.client_id, Number(c.price_per_qualified_rdv));
  }
  // VARIABLE facturée par client × mois = RDV validés/facturés × prix/RDV, rattachée au mois du RDV.
  const varByClient = new Map<string, number[]>();
  for (const a of (appts ?? []) as any[]) {
    const prix = a.client_id ? prixById.get(a.client_id) : undefined;
    if (!prix || !a.date) continue;
    const m = new Date(String(a.date) + 'T00:00:00').getMonth();
    let arr = varByClient.get(a.client_id); if (!arr) { arr = Array(12).fill(0); varByClient.set(a.client_id, arr); }
    arr[m] += prix;
  }
  const varAdded = new Set<string>();
  const lineRows: BpRow[] = (data ?? []).map((r: any) => {
    const baseMonths = Array.from({ length: 12 }, (_, i) => Number(r[`m${i + 1}`]) || 0); // forecast brut (éditable = le fixe)
    const months = [...baseMonths];
    const variableMonths = Array<number>(12).fill(0);
    // Ajoute la variable hybride réelle (RDV validés × prix/RDV) à la ligne du client, une seule fois.
    if (r.client_id && varByClient.has(r.client_id) && !varAdded.has(r.client_id)) {
      const v = varByClient.get(r.client_id)!;
      for (let i = 0; i < 12; i++) { variableMonths[i] = v[i]; months[i] += v[i]; }
      varAdded.add(r.client_id);
    }
    const st = r.client_id ? statusById.get(r.client_id) : null;
    return {
      name: r.name,
      channel: (r.channel as Channel) || 'cold_call',
      contractType: contractTypeFromRaw((r.client_id ? pmById.get(r.client_id) : null) ?? r.contract_type),
      months, baseMonths, variableMonths, pricePerRdv: r.client_id ? (prixById.get(r.client_id) ?? null) : null,
      bpId: r.id ?? null,
      contractMonthly: r.client_id ? (contractMonthlyById.get(r.client_id) ?? null) : null,
      total: months.reduce((s, x) => s + x, 0),
      clientId: r.client_id ?? null,
      status: st === 'actif' ? 'actif' : st === 'inactif' ? 'inactif' : null,
    };
  }).sort((a, b) => b.total - a.total);

  // 1 ligne par CLIENT : les lignes multiples d'un même client (ex. Avenria GTM + Avenria Data)
  // deviennent des sous-lignes d'une ligne parent qui porte la somme.
  const byClient = new Map<string, BpRow[]>();
  const rows: BpRow[] = [];
  for (const r of lineRows) {
    if (!r.clientId) { rows.push(r); continue; }
    if (!byClient.has(r.clientId)) byClient.set(r.clientId, []);
    byClient.get(r.clientId)!.push(r);
  }
  for (const [cid, lines] of byClient) {
    if (lines.length === 1) { rows.push(lines[0]); continue; }
    const sorted = [...lines].sort((a, b) => b.total - a.total);
    const months = Array.from({ length: 12 }, (_, i) => sorted.reduce((s, l) => s + l.months[i], 0));
    const baseMonths = Array.from({ length: 12 }, (_, i) => sorted.reduce((s, l) => s + l.baseMonths[i], 0));
    const variableMonths = Array.from({ length: 12 }, (_, i) => sorted.reduce((s, l) => s + l.variableMonths[i], 0));
    rows.push({
      name: nameById.get(cid) ?? sorted[0].name,
      channel: sorted[0].channel,                    // canal de la ligne principale (la plus grosse)
      contractType: sorted[0].contractType,
      months, baseMonths, variableMonths,
      pricePerRdv: sorted.find(l => l.pricePerRdv != null)?.pricePerRdv ?? null,
      bpId: null,                                     // agrégée : on édite les sous-lignes, pas le parent
      contractMonthly: contractMonthlyById.get(cid) ?? null,
      total: months.reduce((s, x) => s + x, 0),
      clientId: cid, status: sorted[0].status,
      lines: sorted,
    });
  }
  rows.sort((a, b) => b.total - a.total);

  const monthlyTotals = Array.from({ length: 12 }, (_, i) => rows.reduce((s, r) => s + r.months[i], 0));
  const annualTotal = monthlyTotals.reduce((s, x) => s + x, 0);
  const curMonth = new Date().getMonth(); // 0-11
  const activeThisMonth = rows.filter(r => r.months[curMonth] > 0).length;

  // Agrégations : par canal, et croisée canal × formule — sur les LIGNES (pas les parents groupés),
  // sinon un client multi-contrats verserait tout son CA dans le canal de sa ligne principale.
  const accumulate = (keyOf: (r: BpRow) => string) => {
    const m = new Map<string, BpBucket>();
    for (const r of lineRows) {
      const key = keyOf(r);
      let b = m.get(key);
      if (!b) { b = { channel: r.channel, contractType: r.contractType, months: Array(12).fill(0), total: 0, count: 0 }; m.set(key, b); }
      for (let i = 0; i < 12; i++) b.months[i] += r.months[i];
      b.total += r.total; b.count += 1;
    }
    return [...m.values()].sort((a, b) => b.total - a.total);
  };
  const byChannel = accumulate(r => r.channel);
  const byBucket = accumulate(r => `${r.channel}|${r.contractType ?? 'na'}`);
  return { rows, monthlyTotals, annualTotal, activeThisMonth, byChannel, byBucket };
}

/** Édite une cellule du BP (édition inline façon sheet) : forecast de base d'une ligne bp_import.
 *  monthIndex0 = 0..11 (janv..déc). N'affecte QUE la base ; la variable hybride reste calculée au read. */
export async function updateBpMonth(bpId: string, monthIndex0: number, value: number): Promise<void> {
  if (monthIndex0 < 0 || monthIndex0 > 11) throw new Error('mois invalide');
  const col = `m${monthIndex0 + 1}`;
  const { error } = await supabase.from('bp_import').update({ [col]: Number.isFinite(value) ? value : 0 }).eq('id', bpId);
  if (error) throw error;
}

/** Supprime une ligne du BP (bp_import). Pour les lignes fantômes/orphelines. */
export async function deleteBpRow(bpId: string): Promise<void> {
  const { error } = await supabase.from('bp_import').delete().eq('id', bpId);
  if (error) throw error;
}
/** Rattache une ligne BP orpheline à un client (pose le client_id). */
export async function linkBpRowToClient(bpId: string, clientId: string): Promise<void> {
  const { error } = await supabase.from('bp_import').update({ client_id: clientId }).eq('id', bpId);
  if (error) throw error;
}

/** Ajoute une ligne au BP : montant mensuel appliqué de startMonth à endMonth (1-12). */
export async function addBpRow(input: {
  name: string; clientId?: string | null; channel?: Channel; contractType: 'fixed' | 'fixed_plus_variable' | 'gtm'; monthly: number; startMonth: number; endMonth?: number;
}): Promise<void> {
  // Le BP part de la BASE CLIENT : si aucun client existant n'est choisi, on CRÉE le client
  // (entrée d'onboarding, statut actif/pending) + son contrat (calibration), puis on lie la ligne BP.
  let clientId = input.clientId || null;
  if (!clientId && input.name.trim()) {
    clientId = await createOnboardingClient({ name: input.name.trim() });
    await supabase.from('calibrations').insert({
      client_id: clientId, pricing_model: input.contractType, channel: input.channel || 'cold_call',
      fixed_price: input.monthly, is_active: true, status: 'validated',
    });
  }
  const end = input.endMonth || 12;
  const row: any = { name: input.name, channel: input.channel || 'cold_call', contract_type: input.contractType, ref: input.monthly, client_id: clientId };
  for (let m = 1; m <= 12; m++) row[`m${m}`] = (m >= input.startMonth && m <= end) ? input.monthly : 0;
  const { error } = await supabase.from('bp_import').insert(row);
  if (error) throw error;
}

// Compteur de la part VARIABLE hybride (RDV honorés × prix/RDV) sur une fenêtre
// mois précédent / courant / suivant — « combien on ajoute au BP » grâce à l'hybride.
export interface HybrideBillingMonth { ym: string; label: string }
export interface HybrideBillingRow { clientId: string; name: string; pricePerRdv: number; rdvByMonth: number[]; variableByMonth: number[]; total: number }
export interface HybrideBilling { months: HybrideBillingMonth[]; rows: HybrideBillingRow[]; totalByMonth: number[]; grandTotal: number }
/** 1er jour du mois suivant 'YYYY-MM' (pour des bornes de date exclusives, toujours valides). */
function nextMonthFirstDay(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
}
export async function getHybrideBillingWindow(): Promise<HybrideBilling> {
  const now = new Date();
  const months: HybrideBillingMonth[] = [-1, 0, 1].map(off => {
    const d = new Date(now.getFullYear(), now.getMonth() + off, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return { ym, label: d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }) };
  });
  // Prix/RDV lu dans les CONTRATS (source de vérité), plus dans le cache calibrations (audit 08/10).
  const { data: cals } = await supabase.from('client_contracts')
    .select('client_id,price_per_qualified_rdv,clients(name)')
    .eq('is_active', true).eq('pricing_model', 'fixed_plus_variable').not('price_per_qualified_rdv', 'is', null);
  const cs = (cals ?? []).filter((c: any) => c.client_id);
  if (!cs.length) return { months, rows: [], totalByMonth: [0, 0, 0], grandTotal: 0 };
  const ids = cs.map((c: any) => c.client_id);
  const { data: appts } = await supabase.from('appointments')
    .select('client_id,date,lifecycle_status')
    // Borne haute EXCLUSIVE sur le 1er jour du mois suivant : « ym + '-31' » fabriquait des dates
    // invalides (« 2026-11-31 ») → requête en erreur tout le mois, compteur direction absent (audit 08/10).
    .in('client_id', ids).gte('date', months[0].ym + '-01').lt('date', nextMonthFirstDay(months[2].ym))
    .in('lifecycle_status', ['held', 'qualified', 'validated', 'billed']);
  const rows: HybrideBillingRow[] = cs.map((c: any) => {
    const price = Number(c.price_per_qualified_rdv) || 0;
    const rdvByMonth = months.map(m => (appts ?? []).filter((a: any) => a.client_id === c.client_id && String(a.date).slice(0, 7) === m.ym).length);
    const variableByMonth = rdvByMonth.map(n => n * price);
    return { clientId: c.client_id, name: (c.clients as any)?.name ?? '—', pricePerRdv: price, rdvByMonth, variableByMonth, total: variableByMonth.reduce((s, x) => s + x, 0) };
  }).sort((a, b) => b.total - a.total);
  const totalByMonth = months.map((_, i) => rows.reduce((s, r) => s + r.variableByMonth[i], 0));
  return { months, rows, totalByMonth, grandTotal: totalByMonth.reduce((s, x) => s + x, 0) };
}

// ── Facturation mensuelle (compteur encaissable) ────────────────────────
export interface ClientBilling {
  clientId: string;
  name: string;
  channel: Channel;
  contractType: ContractType | null;
  fixed: number;
  pricePerRdv: number;
  rdvValidated: number;   // RDV validés/facturés du mois (hybride uniquement)
  variable: number;       // rdvValidated × pricePerRdv
  total: number;
}

/** Compteur mensuel par client : fixe + (RDV validés × prix/RDV) pour l'hybride.
 *  Forfait/GTM = fixe seul ; jours/non renseigné = ignoré (fixe 0). */
/** Écart BP saisi vs contrats actifs sur le mois courant — le bandeau qui répond aux
 *  « trois CA différents » de Kevin (audit 08/10) : une seule comparaison, visible, au lieu de
 *  trois vérités silencieuses. */
export interface BpEcart { ym: string; bpFixe: number; contratsFixe: number }
export async function getBpEcartContrats(): Promise<BpEcart> {
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const mcol = `m${now.getMonth() + 1}`;
  const [{ data: bps, error: e1 }, { data: fixes, error: e2 }] = await Promise.all([
    supabase.from('bp_import').select(mcol),
    supabase.rpc('rpc_fixe_contrats_mois', { p_ym: ym }),
  ]);
  if (e1) throw e1;
  if (e2) throw e2;
  const bpFixe = (bps ?? []).reduce((t: number, r: any) => t + (Number(r[mcol]) || 0), 0);
  const contratsFixe = ((fixes ?? []) as any[]).reduce((t, r) => t + (Number(r.fixe) || 0), 0);
  return { ym, bpFixe, contratsFixe };
}

export async function getMonthlyBilling(year: number, month: number): Promise<{
  rows: ClientBilling[]; totalFixed: number; totalVariable: number; total: number;
  byChannel: { channel: Channel; total: number; count: number }[];
}> {
  const mm = String(month).padStart(2, '0');
  const start = `${year}-${mm}-01`;
  const end = month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`;

  // FIXE dérivé des CONTRATS fenêtrés sur le mois (rpc_fixe_contrats_mois) — plus jamais de
  // calibrations.fixed_price : c'était la 3e source tarifaire qui divergeait (« trois CA »
  // différents pour Kevin, audit 08/10). Le fenêtrage start/end remplace le filtre statut :
  // un contrat clos le 30/09 compte en septembre, plus en octobre.
  const [{ data: fixes, error: e1 }, { data: actifs, error: e1b }] = await Promise.all([
    supabase.rpc('rpc_fixe_contrats_mois', { p_ym: `${year}-${mm}` }),
    supabase.from('client_contracts').select('client_id, channel, pricing_model, price_per_qualified_rdv').eq('is_active', true),
  ]);
  if (e1) throw e1;
  if (e1b) throw e1b;
  const infoByClient = new Map<string, any>();
  for (const a of (actifs ?? []) as any[]) {
    const cur = infoByClient.get(a.client_id);
    // le contrat hybride prime pour le pricing affiché ; sinon le premier trouvé
    if (!cur || a.pricing_model === 'fixed_plus_variable') infoByClient.set(a.client_id, a);
  }

  // RDV encaissables (validés/facturés) sur le mois, groupés par client
  const { data: appts, error: e2 } = await supabase
    .from('appointments')
    .select('client_id, lifecycle_status, date, validated_at')
    .in('lifecycle_status', ['validated', 'billed']);
  if (e2) throw e2;

  const inMonth = (a: any) => {
    const d = (a.validated_at ? a.validated_at.slice(0, 10) : a.date) || '';
    return d >= start && d < end;
  };
  const validatedByClient = new Map<string, number>();
  (appts ?? []).filter(inMonth).forEach(a => {
    if (a.client_id) validatedByClient.set(a.client_id, (validatedByClient.get(a.client_id) || 0) + 1);
  });

  const rows: ClientBilling[] = ((fixes ?? []) as any[]).map((c: any) => {
    const info = infoByClient.get(c.client_id);
    const type = contractTypeFromRaw(info?.pricing_model);
    const fixed = Number(c.fixe) || 0;
    const pricePerRdv = Number(info?.price_per_qualified_rdv) || 0;
    const rdvValidated = type === 'variable' ? (validatedByClient.get(c.client_id) || 0) : 0;
    const variable = rdvValidated * pricePerRdv;
    return {
      clientId: c.client_id, name: c.client_name || '—',
      channel: ((info?.channel ?? 'cold_call') as Channel), contractType: type,
      fixed, pricePerRdv, rdvValidated, variable, total: fixed + variable,
    };
  }).filter(r => r.total > 0)
    .sort((a, b) => b.total - a.total);

  const totalFixed = rows.reduce((s, r) => s + r.fixed, 0);
  const totalVariable = rows.reduce((s, r) => s + r.variable, 0);
  const chMap = new Map<Channel, { channel: Channel; total: number; count: number }>();
  for (const r of rows) {
    const b = chMap.get(r.channel) || { channel: r.channel, total: 0, count: 0 };
    b.total += r.total; b.count += 1; chMap.set(r.channel, b);
  }
  const byChannel = [...chMap.values()].sort((a, b) => b.total - a.total);
  return { rows, totalFixed, totalVariable, total: totalFixed + totalVariable, byChannel };
}

/** Détail des RDV facturables d'un client sur un mois (hybride) — pour coller dans Tiime (Baptiste).
 *  RDV qualifiés/honorés facturés = lifecycle validated|billed, rattachés au mois de validation (ou date). */
export interface BillableRdv { prospectName: string | null; company: string | null; date: string | null; jobTitle: string | null }
export async function getBillableRdvDetail(clientId: string, year: number, month: number): Promise<BillableRdv[]> {
  const mm = String(month).padStart(2, '0');
  const start = `${year}-${mm}-01`;
  const end = month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const { data, error } = await supabase.from('appointments')
    .select('prospect_name,company_name,job_title,date,validated_at')
    .eq('client_id', clientId).in('lifecycle_status', ['validated', 'billed']);
  if (error) throw error;
  return (data ?? []).filter((a: any) => {
    const d = (a.validated_at ? String(a.validated_at).slice(0, 10) : a.date) || '';
    return d >= start && d < end;
  }).map((a: any) => ({ prospectName: a.prospect_name ?? null, company: a.company_name ?? null, jobTitle: a.job_title ?? null, date: a.date ?? null }))
    .sort((a, b) => (a.company || '').localeCompare(b.company || ''));
}

// ── Suivi de facturation (Baptiste) : qui a été facturé, qui ne l'a pas été ────
export type BillingStatus = 'todo' | 'invoiced' | 'sent';
export const BILLING_STATUS_LABELS: Record<BillingStatus, string> = { todo: 'À facturer', invoiced: 'Facturée', sent: 'Envoyée' };
export async function getBillingChecks(year: number, month: number): Promise<Map<string, BillingStatus>> {
  const { data, error } = await supabase.from('billing_checks').select('client_id,status').eq('year', year).eq('month', month);
  if (error) throw error;
  return new Map((data ?? []).map((r: any) => [r.client_id, r.status as BillingStatus]));
}
export async function setBillingCheck(clientId: string, year: number, month: number, status: BillingStatus, userId?: string | null): Promise<void> {
  const { error } = await supabase.from('billing_checks')
    .upsert({ client_id: clientId, year, month, status, updated_by: userId ?? null, updated_at: new Date().toISOString() }, { onConflict: 'client_id,year,month' });
  if (error) throw error;
}

// ── Day to Day (cockpit quotidien) ──────────────────────────────────────
export interface DayRdv extends RdvRow {
  sdr_id: string | null;
  time: string | null;
  phone: string | null;
  created_at: string | null;
  call_id: string | null;
  prospect_linkedin: string | null;
  email_propsect: string | null;
  notes: string | null;
  sdr_comments: string | null;
  description: string | null;
  location: string | null;
  /** Note laissée par le client pour le SDR (distincte de `notes`, interne). */
  client_note: string | null;
  client_note_at: string | null;
  sdr?: { sales_name: string | null } | null;
}

const DAY_SELECT =
  'id,prospect_name,company_name,job_title,phone,time,date,created_at,call_id,pipedrive_deal_id,lifecycle_status,icp_conformant,source,client_id,sdr_id,' +
  'prospect_linkedin,email_propsect,notes,sdr_comments,description,location,client_note,client_note_at,' +
  'clients(name),calibration:calibrations(pricing_model),sdr:profiles!appointments_sdr_id_fkey(sales_name)';

/** Résultat de l'évaluation IA de l'appel qui a produit ce RDV (lien via call_id = call_log_id). */
export interface CallEvaluation {
  score_global: number | null;
  grade: string | null;
  top_strengths: unknown;   // jsonb : parfois array, souvent string — normalisé par toList()
  top_weaknesses: unknown;
  action_recommendation: string | null;
  verdict_follow_up: string | null;
  evaluated_at: string | null;
}

export async function getEvaluationByCallId(callId: string): Promise<CallEvaluation | null> {
  const { data, error } = await supabase
    .from('sdr_call_evaluations')
    .select('score_global,grade,top_strengths,top_weaknesses,action_recommendation,verdict_follow_up,evaluated_at')
    .eq('call_log_id', callId)
    .order('evaluated_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  return (data && data.length ? (data[0] as CallEvaluation) : null);
}

/** Ajout d'un RDV à la main (filet quand l'automation mail / cold call ne l'a pas créé).
 *  Statut initial = 'posed' (Booké) ; on fixe aussi le `status` legacy pour le front historique. */
export interface ManualRdvInput {
  clientId: string;
  sdrId: string | null;
  /** Provenance du RDV (spec-provenance-rdv) : obligatoire à la création manuelle — condition
   *  pour résorber les 4 619 sources null (audit 08/10). */
  source: 'call' | 'email' | 'linkedin' | 'entrant';
  prospectName: string;
  company?: string | null;
  jobTitle?: string | null;
  phone?: string | null;
  email?: string | null;
  linkedin?: string | null;
  date: string;   // YYYY-MM-DD
  time: string;   // HH:MM
  notes?: string | null;
  icpConformant?: boolean | null;
}
export async function createManualAppointment(input: ManualRdvInput): Promise<string> {
  const title = [input.prospectName, input.company].filter(Boolean).join(' — ') || 'RDV';
  const row: any = {
    client_id: input.clientId,
    sdr_id: input.sdrId || null,
    prospect_name: input.prospectName,
    company_name: input.company || null,
    job_title: input.jobTitle || null,
    phone: input.phone || null,
    email_propsect: input.email || null,          // (typo de colonne côté DB : email_propsect)
    prospect_linkedin: input.linkedin || null,
    date: input.date,
    time: input.time,
    title,
    status: 'planifié',                            // legacy → mappé 'posed' par le trigger si besoin
    lifecycle_status: 'posed',                     // canonique : Booké
    icp_conformant: input.icpConformant ?? null,
    notes: input.notes || null,
    source: input.source,
  };
  const { data, error } = await supabase.from('appointments').insert([row]).select('id').limit(1);
  if (error) throw error;
  return (data?.[0] as any)?.id;
}

/** Un RDV complet (forme DayRdv) par son id — pour la fiche détail. */
export async function getAppointmentDetail(id: string): Promise<DayRdv | null> {
  const { data, error } = await supabase.from('appointments').select(DAY_SELECT).eq('id', id).limit(1);
  if (error) throw error;
  return (data && data.length ? (data[0] as DayRdv) : null);
}

/** L'appel qui a produit le RDV : résumé + enregistrement (via call_id = call_logs.id). */
export interface CallDetail { call_summary: string | null; recording_url: string | null; when: string | null; duration_seconds: number | null; }
export async function getCallDetail(callId: string): Promise<CallDetail | null> {
  const { data, error } = await supabase.from('call_logs')
    .select('call_summary,recording_storage_url,recording_temp_url,created_at,duration_seconds')
    .eq('id', callId).limit(1);
  if (error) throw error;
  if (!data || !data.length) return null;
  const r: any = data[0];
  // recording_storage_url est un CHEMIN dans le bucket `calls` (ex. « calls/xxx.mp3 »), pas une
  // URL. Sécu 08/10 : le bucket n'est plus public → URL SIGNÉE (1 h), générée avec le JWT de
  // l'utilisateur (la RLS storage décide qui peut signer quoi : interne = tout, client = ses calls).
  // Repli sur l'URL temp Minari (souvent expirée ~24h) si pas de storage.
  let recording_url: string | null = null;
  if (r.recording_storage_url) {
    const key = String(r.recording_storage_url).replace(/^calls\//, '');
    const { data: signed } = await supabase.storage.from('calls').createSignedUrl(key, 3600);
    recording_url = signed?.signedUrl ?? null;
  }
  if (!recording_url && r.recording_temp_url) recording_url = r.recording_temp_url;
  return {
    call_summary: r.call_summary ?? null,
    recording_url,
    when: r.created_at ?? null,
    duration_seconds: r.duration_seconds ?? null,
  };
}

/** Reporte un RDV : change date/heure + journalise la transition 'rescheduled'. */
// ── Outreach (mail Instantly / LinkedIn HeyReach) : campagnes + réponses par client ──────
// provider 'instantly' = mail, 'heyreach' = LinkedIn. Données live depuis les webhooks.
export interface OutreachChannelStat { campaigns: number; replies: number; positives: number; sent: number }
export interface OutreachReplyItem { at: string; channel: 'mail' | 'linkedin'; prospect: string | null; company: string | null; message: string | null; positive: boolean }
export interface ClientOutreach { mail: OutreachChannelStat; linkedin: OutreachChannelStat; recent: OutreachReplyItem[]; hasAny: boolean }

const outreachChan = (provider: string | null) => (provider === 'heyreach' ? 'linkedin' : 'mail') as 'mail' | 'linkedin';

export async function getClientOutreach(clientId: string, fromISO?: string, toISO?: string): Promise<ClientOutreach> {
  // Fenêtre jours (YYYY-MM-DD) pour les ENVOYÉS (outreach_stats_daily — volumes réels Instantly/HeyReach)
  const fromDay = fromISO ? fromISO.slice(0, 10) : '2000-01-01';
  const toDay = toISO ? toISO.slice(0, 10) : '2999-12-31';
  const [{ data: camps }, { data: replies }, { data: sentRows }] = await Promise.all([
    supabase.from('outreach_campaigns').select('provider,channel').eq('client_id', clientId),
    supabase.from('outreach_replies')
      .select('provider,classification,prospect_firstname,prospect_lastname,prospect_company,message_text,created_at,shadow')
      .eq('client_id', clientId).order('created_at', { ascending: false }).limit(500),
    supabase.from('outreach_stats_daily').select('channel,sent').eq('client_id', clientId).gte('date', fromDay).lte('date', toDay),
  ]);
  const mail: OutreachChannelStat = { campaigns: 0, replies: 0, positives: 0, sent: 0 };
  const linkedin: OutreachChannelStat = { campaigns: 0, replies: 0, positives: 0, sent: 0 };
  for (const r of (sentRows ?? []) as any[]) (r.channel === 'linkedin' ? linkedin : mail).sent += Number(r.sent) || 0;
  for (const c of (camps ?? []) as any[]) (outreachChan(c.provider) === 'linkedin' ? linkedin : mail).campaigns++;
  const recent: OutreachReplyItem[] = [];
  for (const r of (replies ?? []) as any[]) {
    if (r.shadow) continue;
    if (fromISO && r.created_at < fromISO) continue;
    if (toISO && r.created_at > toISO) continue;
    const ch = outreachChan(r.provider);
    const t = ch === 'linkedin' ? linkedin : mail;
    t.replies++;
    const positive = r.classification === 'positive';
    if (positive) t.positives++;
    recent.push({
      at: r.created_at, channel: ch, positive,
      prospect: [r.prospect_firstname, r.prospect_lastname].filter(Boolean).join(' ') || null,
      company: r.prospect_company ?? null, message: r.message_text ?? null,
    });
  }
  const hasAny = mail.campaigns + linkedin.campaigns + recent.length + mail.sent + linkedin.sent > 0;
  return { mail, linkedin, recent, hasAny };
}

// ── Outreach — agrégat GLOBAL par client (vue interne campagnes + registre) ────────────────
export interface OutreachAgg {
  mailCampaigns: number; mailReplies: number; mailPositives: number;
  liCampaigns: number; liReplies: number; liPositives: number;
  lastReplyAt: string | null;
}
const emptyAgg = (): OutreachAgg => ({ mailCampaigns: 0, mailReplies: 0, mailPositives: 0, liCampaigns: 0, liReplies: 0, liPositives: 0, lastReplyAt: null });

/** Agrège campagnes + réponses mail/LinkedIn par client_id (une Map). Partagé par la vue
 *  globale des campagnes et l'enrichissement du registre. provider heyreach = LinkedIn. */
async function fetchOutreachByClient(): Promise<Map<string, OutreachAgg>> {
  const [{ data: camps }, { data: replies }] = await Promise.all([
    supabase.from('outreach_campaigns').select('client_id,provider'),
    supabase.from('outreach_replies').select('client_id,provider,classification,created_at,shadow'),
  ]);
  const m = new Map<string, OutreachAgg>();
  const get = (id: string) => { let a = m.get(id); if (!a) { a = emptyAgg(); m.set(id, a); } return a; };
  for (const c of (camps ?? []) as any[]) { if (!c.client_id) continue; const a = get(c.client_id); if (c.provider === 'heyreach') a.liCampaigns++; else a.mailCampaigns++; }
  for (const r of (replies ?? []) as any[]) {
    if (!r.client_id || r.shadow) continue;
    const a = get(r.client_id); const li = r.provider === 'heyreach'; const pos = r.classification === 'positive';
    if (li) { a.liReplies++; if (pos) a.liPositives++; } else { a.mailReplies++; if (pos) a.mailPositives++; }
    if (r.created_at && (!a.lastReplyAt || r.created_at > a.lastReplyAt)) a.lastReplyAt = r.created_at;
  }
  return m;
}

// Détail par campagne pour un client (registre des campagnes côté portail)
export interface CampaignDetail {
  campaignId: string; name: string; channel: 'mail' | 'linkedin'; status: string | null;
  replies: number; positives: number; lastReplyAt: string | null;
  recentReplies: OutreachReplyItem[];
}
/** Campagnes d'un client + leurs réponses groupées (registre campagnes). Données live. */
export async function getClientCampaigns(clientId: string): Promise<CampaignDetail[]> {
  const [{ data: camps }, { data: replies }] = await Promise.all([
    supabase.from('outreach_campaigns').select('provider,campaign_id,campaign_name,channel,active').eq('client_id', clientId),
    supabase.from('outreach_replies')
      .select('provider,campaign_id,classification,prospect_firstname,prospect_lastname,prospect_company,message_text,created_at,shadow')
      .eq('client_id', clientId).order('created_at', { ascending: false }).limit(1000),
  ]);
  const key = (prov: string, cid: string | null) => `${prov}|${cid ?? ''}`;
  const map = new Map<string, CampaignDetail>();
  for (const c of (camps ?? []) as any[]) {
    map.set(key(c.provider, c.campaign_id), {
      campaignId: c.campaign_id, name: c.campaign_name || c.campaign_id || '(sans nom)',
      channel: outreachChan(c.provider), status: c.active === false ? 'inactive' : 'active',
      replies: 0, positives: 0, lastReplyAt: null, recentReplies: [],
    });
  }
  for (const r of (replies ?? []) as any[]) {
    if (r.shadow) continue;
    const k = key(r.provider, r.campaign_id);
    let d = map.get(k);
    if (!d) { // réponse sur une campagne pas (encore) listée
      d = { campaignId: r.campaign_id, name: r.campaign_id || '(campagne inconnue)', channel: outreachChan(r.provider), status: null, replies: 0, positives: 0, lastReplyAt: null, recentReplies: [] };
      map.set(k, d);
    }
    d.replies++;
    const positive = r.classification === 'positive';
    if (positive) d.positives++;
    if (r.created_at && (!d.lastReplyAt || r.created_at > d.lastReplyAt)) d.lastReplyAt = r.created_at;
    if (d.recentReplies.length < 50) d.recentReplies.push({
      at: r.created_at, channel: outreachChan(r.provider), positive,
      prospect: [r.prospect_firstname, r.prospect_lastname].filter(Boolean).join(' ') || null,
      company: r.prospect_company ?? null, message: r.message_text ?? null,
    });
  }
  return [...map.values()].sort((a, b) => b.replies - a.replies || (b.lastReplyAt ?? '').localeCompare(a.lastReplyAt ?? ''));
}

export interface OutreachOverviewRow extends OutreachAgg { clientId: string; clientName: string; statut: string | null }
/** Vue interne globale : tous les clients ayant de l'activité mail/LinkedIn, triés par réponses. */
export async function getOutreachOverview(): Promise<OutreachOverviewRow[]> {
  const map = await fetchOutreachByClient();
  const ids = [...map.keys()];
  if (!ids.length) return [];
  const { data: cs } = await supabase.from('clients').select('id,name,statut').in('id', ids);
  const nameMap = new Map((cs ?? []).map((c: any) => [c.id, c]));
  return ids.map(id => ({ clientId: id, clientName: nameMap.get(id)?.name ?? '—', statut: nameMap.get(id)?.statut ?? null, ...map.get(id)! }))
    .sort((a, b) => (b.mailReplies + b.liReplies) - (a.mailReplies + a.liReplies));
}

/** URL de lecture de l'enregistrement d'un appel. null si pas de recording.
 *  Sécu 08/10 : bucket `calls` privé → URL SIGNÉE 1 h (la RLS storage filtre par rôle/client).
 *  Préfère le fichier stocké (durable) ; sinon l'URL temporaire Minari. */
export async function getCallRecordingUrl(callId: string): Promise<string | null> {
  const { data } = await supabase.from('call_logs')
    .select('recording_storage_url, recording_temp_url').eq('id', callId).limit(1).maybeSingle();
  if (!data) return null;
  const key = (data as any).recording_storage_url as string | null;
  if (key) {
    const { data: signed } = await supabase.storage.from('calls')
      .createSignedUrl(key.replace(/^calls\//, ''), 3600);
    if (signed?.signedUrl) return signed.signedUrl;
  }
  return (data as any).recording_temp_url || null;
}

/** Édite les champs d'un RDV (correction prospect / réattribution client & SDR). RLS : interne
 *  (sdr/csm/admin/super_admin) autorisé en UPDATE sur appointments. */
export async function updateAppointmentFields(id: string, patch: Record<string, any>): Promise<void> {
  const { error } = await supabase.from('appointments').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
}

/** Note du client pour le SDR. Via RPC (le client n'a pas d'UPDATE direct sur appointments) :
 *  réservée aux comptes client, contrôle d'appartenance + trace dans appointment_events. */
export async function setClientNote(appointmentId: string, note: string): Promise<void> {
  const { error } = await supabase.rpc('set_client_note', {
    p_appointment_id: appointmentId,
    p_note: normalizeClientNote(note),
  });
  if (error) throw error;
}

/** Tous les RDV sur une plage (page de recherche RDV). Triés par date décroissante. */
export async function searchAppointments(fromDate: string, toDate: string): Promise<DayRdv[]> {
  const { data, error } = await supabase.from('appointments').select(DAY_SELECT)
    .gte('date', fromDate).lte('date', toDate)
    .order('date', { ascending: false }).order('time', { ascending: false }).limit(4000);
  if (error) throw error;
  return (data ?? []) as unknown as DayRdv[];
}

/** RDV d'un SDR sur une plage (drill-down Stats d'équipe). */
export async function getSdrAppointmentsInRange(sdrId: string, fromISO: string, toISO: string): Promise<DayRdv[]> {
  const { data, error } = await supabase.from('appointments').select(DAY_SELECT)
    .eq('sdr_id', sdrId).gte('created_at', fromISO).lte('created_at', toISO)
    .order('date', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as DayRdv[];
}

export async function rescheduleAppointment(id: string, newDate: string, newTime: string) {
  const { error } = await supabase.from('appointments').update({ date: newDate, time: newTime }).eq('id', id);
  if (error) throw error;
  await advanceAppointment(id, 'rescheduled', { note: `Reporté au ${newDate} ${newTime}` });
}

/** RDV planifiés pour une date donnée (colonne `date`), triés par heure. */
export async function getDayAppointments(dateStr: string): Promise<DayRdv[]> {
  const { data, error } = await supabase
    .from('appointments').select(DAY_SELECT)
    .eq('date', dateStr).order('time', { ascending: true });
  if (error) throw error;
  return (data ?? []) as DayRdv[];
}

/** RDV créés pendant une date donnée (fenêtre created_at locale), triés récents d'abord. */
export async function getCreatedOnDate(dateStr: string): Promise<DayRdv[]> {
  const [y, m, d] = dateStr.split('-').map(Number);
  const start = new Date(y, m - 1, d).toISOString();
  const end = new Date(y, m - 1, d + 1).toISOString();
  const { data, error } = await supabase
    .from('appointments').select(DAY_SELECT)
    .gte('created_at', start).lt('created_at', end)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as DayRdv[];
}

/** RDV bookés (créés) sur une plage inclusive [fromStr, toStr] — pour la vue semaine Production SDR. */
export async function getCreatedBetween(fromStr: string, toStr: string): Promise<DayRdv[]> {
  const [fy, fm, fd] = fromStr.split('-').map(Number);
  const [ty, tm, td] = toStr.split('-').map(Number);
  const start = new Date(fy, fm - 1, fd).toISOString();
  const end = new Date(ty, tm - 1, td + 1).toISOString();
  const { data, error } = await supabase
    .from('appointments').select(DAY_SELECT)
    .gte('created_at', start).lt('created_at', end)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as DayRdv[];
}

// ── Rapports mensuels client (brouillon CSM → publié) ───────────────────
// Blocs typés : text (narratif éditable), kpis (auto figé), table (auto ou IA),
// bullets (objections/personas/reco). Chaque bloc : visible on/off.
export type ReportBlock =
  | { id: string; type: 'text'; title: string; body: string; hidden?: boolean }
  | { id: string; type: 'kpis'; title: string; items: { label: string; value: string; hint?: string }[]; hidden?: boolean }
  | { id: string; type: 'table'; title: string; columns: string[]; rows: string[][]; note?: string; hidden?: boolean }
  | { id: string; type: 'bullets'; title: string; items: string[]; hidden?: boolean }
  | { id: string; type: 'chart'; chart: 'bar' | 'pie'; title: string; data: { label: string; value: number }[]; hidden?: boolean };

export interface ReportKpis {
  calls: number; connected: number; rdvBooked: number; rdvHeld: number; rdvQualified: number; noShow: number;
}
export interface ClientReport {
  id: string; client_id: string; period_year: number; period_month: number;
  title: string | null; status: 'draft' | 'published';
  source: 'generated' | 'uploaded'; file_path: string | null;
  kpis: ReportKpis; sections: ReportBlock[];
  published_at: string | null; updated_at: string | null;
}

const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const REPORT_COLS = 'id,client_id,period_year,period_month,title,status,source,file_path,kpis,sections,published_at,updated_at';

/** Coerce les anciennes sections {id,title,body} en blocs 'text' (rétro-compat). */
function normalizeBlocks(sections: any): ReportBlock[] {
  if (!Array.isArray(sections)) return [];
  return sections.map((s: any) => (s && s.type ? s : { id: s?.id ?? 'b', type: 'text', title: s?.title ?? '', body: s?.body ?? '' }));
}
const pctStr = (n: number, d: number) => (d > 0 ? `${Math.round((n / d) * 1000) / 10} %` : '—');
const weekStartISO = (iso: string) => {
  const d = new Date(iso + 'T00:00:00'); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
};

/** Liste des rapports d'un client (RLS : le client ne voit que les publiés). */
export async function listReports(clientId: string): Promise<ClientReport[]> {
  const { data, error } = await supabase.from('client_reports')
    .select(REPORT_COLS)
    .eq('client_id', clientId)
    .order('period_year', { ascending: false }).order('period_month', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r: any) => ({ ...r, sections: normalizeBlocks(r.sections) })) as ClientReport[];
}

export async function getReport(id: string): Promise<ClientReport | null> {
  const { data, error } = await supabase.from('client_reports')
    .select(REPORT_COLS)
    .eq('id', id).limit(1);
  if (error) throw error;
  if (!data || !data.length) return null;
  const r: any = data[0];
  return { ...r, sections: normalizeBlocks(r.sections) } as ClientReport;
}

/** Agrège les blocs DÉTERMINISTES d'un rapport sur une plage [startISO, endISO) :
 *  KPIs, taux de conversion, évolution mensuelle, statut, détail RDV, hebdo, objections, ICP.
 *  Réutilisé par le rapport mensuel (CSM) ET l'analyse live du client (1M/3M/all). */
async function buildReportBlocks(clientId: string, startISO: string, endISO: string): Promise<{ kpis: ReportKpis; blocks: ReportBlock[] }> {
  const [callsRes, apptsRes, calRes, ctxRes] = await Promise.all([
    supabase.from('call_logs').select('duration_seconds,status,sdr_name,created_at')
      .eq('client_id', clientId).gte('created_at', startISO).lte('created_at', endISO).limit(15000),
    supabase.from('appointments').select('date,prospect_name,job_title,company_name,lifecycle_status,created_at,sdr:profiles!appointments_sdr_id_fkey(sales_name)')
      .eq('client_id', clientId).gte('created_at', startISO).lte('created_at', endISO).order('date', { ascending: true }),
    supabase.from('calibrations').select('sectors,zones,target_functions,exclusions,size_min,size_max').eq('client_id', clientId).eq('is_active', true).limit(1),
    supabase.from('client_context').select('icp_summary').eq('client_id', clientId).order('version', { ascending: false }).limit(1),
  ]);

  const callRows = (callsRes.data ?? []) as any[];
  const arows = (apptsRes.data ?? []) as any[];

  // ── Volume RÉEL depuis la téléphonie (call_logs), définitions du dictionnaire (§2) :
  //    connecté = décroché (status), conversation = connecté > 30s. « Appels passés » a été retiré
  //    de l'affichage (audit 08/10) : call_logs historique ne contient pas les tentatives sans
  //    décroché → le chiffre était incomparable à la V1 (déclaratif SDR, jusqu'à 6× plus). ──
  const dayKey = (iso: string) => (iso || '').slice(0, 10);
  const connectedRows = callRows.filter(c => c.status === 'connected');
  const connectes = connectedRows.length;
  const conversations = connectedRows.filter(c => (Number(c.duration_seconds) || 0) > 30).length;
  const joursProspectes = new Set(callRows.map(c => dayKey(c.created_at)).filter(Boolean)).size;
  const connDurs = connectedRows.map(c => Number(c.duration_seconds) || 0);
  const dureeMax = connDurs.length ? Math.max(...connDurs) : 0;
  const tempsTotal = connDurs.reduce((s, d) => s + d, 0);
  const convDurs = connDurs.filter(d => d > 30);
  const dureeMoy = convDurs.length ? Math.round(convDurs.reduce((s, d) => s + d, 0) / convDurs.length) : 0;
  const fmtDur = (s: number) => {
    if (!s) return '—';
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.round(s % 60);
    return h ? `${h}h ${m}min` : m ? `${m}min ${sec}s` : `${sec}s`;
  };

  const inSet = (s: LifecycleStatus[]) => arows.filter(r => r.lifecycle_status && s.includes(r.lifecycle_status)).length;
  const rdvBooked = arows.length;
  const rdvHeld = inSet(['held', 'qualified', 'not_qualified', 'validated', 'disputed', 'billed']);
  const rdvQualified = inSet(['qualified', 'validated', 'billed']);
  const noShow = inSet(['no_show']);
  const kpis: ReportKpis = { calls: connectes, connected: conversations, rdvBooked, rdvHeld, rdvQualified, noShow };

  // RDV par jour
  const evoMap = new Map<string, number>();
  for (const r of arows) { const k = dayKey(r.created_at); if (k) evoMap.set(k, (evoMap.get(k) || 0) + 1); }
  const evoData = [...evoMap.entries()].sort().map(([k, v]) => ({ label: `${k.slice(8, 10)}/${k.slice(5, 7)}`, value: v }));

  // Distribution des durées de conversation (>30s)
  const buckets = [
    { label: '31-60s', test: (d: number) => d > 30 && d <= 60 },
    { label: '1-2min', test: (d: number) => d > 60 && d <= 120 },
    { label: '2-3min', test: (d: number) => d > 120 && d <= 180 },
    { label: '3-5min', test: (d: number) => d > 180 && d <= 300 },
    { label: '5min+', test: (d: number) => d > 300 },
  ];
  const distData = buckets.map(b => ({ label: b.label, value: convDurs.filter(b.test).length }));

  // Répartition par statut (chart + table)
  const statusMap = new Map<string, number>();
  for (const r of arows) { const s = r.lifecycle_status || 'posed'; statusMap.set(s, (statusMap.get(s) || 0) + 1); }
  const statusEntries = [...statusMap.entries()].sort((a, b) => b[1] - a[1]);
  const statusData = statusEntries.map(([s, n]) => ({ label: LIFECYCLE[s as LifecycleStatus]?.label ?? s, value: n }));
  const statusRows = statusEntries.map(([s, n]) => [LIFECYCLE[s as LifecycleStatus]?.label ?? s, String(n), pctStr(n, rdvBooked)]);

  // Performance par SDR (call_logs : appels/conv/durée ; RDV via appointments)
  const rdvBySdr = new Map<string, number>();
  for (const r of arows) { const n = (r.sdr?.sales_name || '').trim(); if (n) rdvBySdr.set(n, (rdvBySdr.get(n) || 0) + 1); }
  const sdrMap = new Map<string, { appels: number; conv: number; durSum: number; durN: number }>();
  for (const c of callRows) {
    const n = (c.sdr_name || '').trim(); if (!n) continue;
    const e = sdrMap.get(n) || { appels: 0, conv: 0, durSum: 0, durN: 0 };
    const d = Number(c.duration_seconds) || 0;
    const cx = c.status === 'connected';
    if (cx) { e.appels++; if (d > 30) e.conv++; e.durSum += d; e.durN++; }
    sdrMap.set(n, e);
  }
  const sdrRows = [...sdrMap.entries()].sort((a, b) => b[1].appels - a[1].appels).map(([n, e]) => [
    n, String(e.appels), String(e.conv), fmtDur(e.durN ? Math.round(e.durSum / e.durN) : 0), String(rdvBySdr.get(n) || 0),
  ]);

  // Détail journalier (call_logs + RDV appointments)
  const dayMap = new Map<string, { appels: number; conv: number; rdv: number }>();
  for (const c of callRows) { const k = dayKey(c.created_at); if (!k) continue; const e = dayMap.get(k) || { appels: 0, conv: 0, rdv: 0 }; if (c.status === 'connected') { e.appels++; if ((Number(c.duration_seconds) || 0) > 30) e.conv++; } dayMap.set(k, e); }
  for (const r of arows) { const k = dayKey(r.created_at); if (!k) continue; const e = dayMap.get(k) || { appels: 0, conv: 0, rdv: 0 }; e.rdv++; dayMap.set(k, e); }
  const fmtD = (d: string | null) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR') : '—');
  const dailyRows = [...dayMap.entries()].sort().slice(-62).map(([k, e]) => [fmtD(k), String(e.appels), String(e.conv), String(e.rdv)]);

  // Détail des RDV (cap 80)
  const detailRows = arows.slice(0, 80).map(r => [
    fmtD(r.date), r.prospect_name || '—', r.job_title || '—', r.company_name || '—',
    LIFECYCLE[r.lifecycle_status as LifecycleStatus]?.label ?? (r.lifecycle_status || '—'),
  ]);

  // Ventilation par semaine (call_logs + RDV appointments)
  const weekMap = new Map<string, { calls: number; conv: number; durSum: number; durN: number; rdv: number }>();
  for (const c of callRows) {
    const k = dayKey(c.created_at); if (!k) continue;
    const wk = weekStartISO(k);
    const w = weekMap.get(wk) || { calls: 0, conv: 0, durSum: 0, durN: 0, rdv: 0 };
    const d = Number(c.duration_seconds) || 0;
    w.calls++; if (d > 30) w.conv++; if (d > 0) { w.durSum += d; w.durN++; }
    weekMap.set(wk, w);
  }
  for (const r of arows) { const k = dayKey(r.created_at); if (!k) continue; const wk = weekStartISO(k); const w = weekMap.get(wk) || { calls: 0, conv: 0, durSum: 0, durN: 0, rdv: 0 }; w.rdv++; weekMap.set(wk, w); }
  const weekRows = [...weekMap.entries()].sort().map(([wk, w]) => [
    `Sem. ${new Date(wk + 'T00:00:00').toLocaleDateString('fr-FR')}`, String(w.calls), String(w.conv), pctStr(w.conv, w.calls), fmtDur(w.durN ? Math.round(w.durSum / w.durN) : 0), String(w.rdv),
  ]);

  // ICP & cibles
  const cal = (calRes.data && calRes.data[0]) as any;
  const icpItems: string[] = [];
  if (cal?.sectors?.length) icpItems.push(`Secteurs : ${cal.sectors.join(', ')}`);
  if (cal?.target_functions?.length) icpItems.push(`Fonctions cibles : ${cal.target_functions.join(', ')}`);
  if (cal?.zones?.length) icpItems.push(`Zones : ${cal.zones.join(', ')}`);
  if (cal?.size_min || cal?.size_max) icpItems.push(`Taille : ${cal.size_min ?? '?'}–${cal.size_max ?? '?'} salariés`);
  if (cal?.exclusions?.length) icpItems.push(`Exclusions : ${cal.exclusions.join(', ')}`);
  const icpSummary = ctxRes.data && ctxRes.data[0]?.icp_summary;
  if (icpSummary) icpItems.unshift(String(icpSummary));

  const blocks: ReportBlock[] = [
    { id: 'kpisVolume', type: 'kpis', title: 'Volume du mois', items: [
      { label: 'Connectés (décrochés)', value: String(connectes) },
      { label: 'Conversations (>30s)', value: String(conversations) },
      { label: 'Taux conversation', value: pctStr(conversations, connectes) },
      { label: 'Jours prospectés', value: String(joursProspectes) },
      { label: 'Connectés / jour', value: joursProspectes ? String(Math.round(connectes / joursProspectes)) : '—' },
    ] },
    { id: 'kpisResults', type: 'kpis', title: 'Résultats · rendez-vous', items: [
      { label: 'Bookés', value: String(rdvBooked) },
      { label: 'Honorés', value: String(rdvHeld) },
      { label: 'Qualifiés', value: String(rdvQualified) },
      { label: 'No-show', value: String(noShow) },
      { label: 'Taux booké / connecté', value: pctStr(rdvBooked, connectes) },
      { label: 'Taux booké / conv.', value: pctStr(rdvBooked, conversations) },
    ] },
    { id: 'kpisQuality', type: 'kpis', title: "Qualité d'appel", items: [
      { label: 'Durée moy. conv.', value: fmtDur(dureeMoy) },
      { label: 'Durée max', value: fmtDur(dureeMax) },
      { label: 'Temps total', value: fmtDur(tempsTotal) },
    ] },
    { id: 'evo', type: 'chart', chart: 'bar', title: 'Bookés par jour', data: evoData },
    { id: 'dist', type: 'chart', chart: 'bar', title: 'Distribution des durées de conversation', data: distData },
    { id: 'statutChart', type: 'chart', chart: 'pie', title: 'Répartition des rendez-vous par statut', data: statusData },
    { id: 'conv', type: 'table', title: 'Taux de conversion', columns: ['Indicateur', 'Valeur', 'Lecture'], rows: [
      ['Conversation / connecté', pctStr(conversations, connectes), `${conversations} conversations sur ${connectes} connectés`],
      ['Conversation → booké', pctStr(rdvBooked, conversations), `${rdvBooked} bookés sur ${conversations} conversations`],
      ['Connecté → booké', pctStr(rdvBooked, connectes), `${rdvBooked} bookés sur ${connectes} connectés`],
      ["Taux d'honoration", pctStr(rdvHeld, rdvHeld + noShow), `${rdvHeld} honorés / ${rdvHeld + noShow}`],
    ] },
    { id: 'statut', type: 'table', title: 'Répartition des rendez-vous par statut', columns: ['Statut', 'Nombre', 'Part'], rows: statusRows },
    { id: 'semaine', type: 'table', title: 'Ventilation par semaine', columns: ['Semaine', 'Appels', 'Conv.', 'Taux conv.', 'Durée moy.', 'Bookés'], rows: weekRows },
    { id: 'sdr', type: 'table', title: 'Performance par SDR', columns: ['SDR', 'Connectés', 'Conversations', 'Durée moy.', 'Bookés'], rows: sdrRows },
    { id: 'daily', type: 'table', title: 'Détail journalier', columns: ['Date', 'Connectés', 'Conversations', 'Bookés'], rows: dailyRows },
    { id: 'detail', type: 'table', title: 'Détail des rendez-vous', columns: ['Date', 'Prospect', 'Fonction', 'Entreprise', 'Statut'], rows: detailRows },
    { id: 'icp', type: 'bullets', title: 'ICP & cibles', items: icpItems },
  ];
  return { kpis, blocks };
}

/** Plage de dates pour l'analyse live client. 'month' = un mois civil précis (year+month). */
export type RangeKind = '1m' | '3m' | 'all' | 'month';
export function rangeFor(kind: RangeKind, year?: number, month?: number): { fromISO: string; toISO: string } {
  const now = new Date();
  if (kind === 'month' && year && month) {
    return { fromISO: new Date(year, month - 1, 1).toISOString(), toISO: new Date(year, month, 0, 23, 59, 59).toISOString() };
  }
  const toISO = now.toISOString();
  if (kind === 'all') return { fromISO: new Date(2020, 0, 1).toISOString(), toISO };
  const months = kind === '3m' ? 3 : 1;
  return { fromISO: new Date(now.getFullYear(), now.getMonth() - months, now.getDate()).toISOString(), toISO };
}

/** Analyse live d'un client sur une plage (déterministe, sans persistance). */
export async function getClientAnalytics(clientId: string, fromISO: string, toISO: string): Promise<{ kpis: ReportKpis; blocks: ReportBlock[] }> {
  return buildReportBlocks(clientId, fromISO, toISO);
}

/** Génère (ou récupère) le brouillon du mois : KPIs + blocs auto (tableaux/objections
 *  depuis la donnée réelle). Le narratif (analyse, concurrents, reco) reste à rédiger /
 *  générer par l'IA. Déterministe et figé à la génération. */
export async function generateReport(clientId: string, year: number, month: number): Promise<ClientReport> {
  const existing = await supabase.from('client_reports').select('id').eq('client_id', clientId).eq('period_year', year).eq('period_month', month).limit(1);
  if (existing.data && existing.data.length) return (await getReport(existing.data[0].id))!;

  const startISO = new Date(year, month - 1, 1).toISOString();
  const endISO = new Date(year, month, 1).toISOString();
  const { kpis, blocks } = await buildReportBlocks(clientId, startISO, endISO);

  const { data, error } = await supabase.from('client_reports').insert({
    client_id: clientId, period_year: year, period_month: month,
    title: `Rapport — ${MONTHS_FR[month - 1]} ${year}`,
    status: 'draft', source: 'generated', kpis, sections: blocks,
    created_by: (await supabase.auth.getUser()).data.user?.id ?? null,
  }).select('id').limit(1);
  if (error) throw error;
  return (await getReport(data![0].id))!;
}

/** Enregistre le brouillon (titre + blocs). */
export async function saveReport(id: string, patch: { title?: string; sections?: ReportBlock[] }): Promise<void> {
  const { error } = await supabase.from('client_reports')
    .update({ ...patch, updated_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
}

/** Upload d'un PDF de rapport existant (bucket reports) → crée/maj un rapport source='uploaded'. */
export async function uploadReportPdf(clientId: string, year: number, month: number, file: File): Promise<void> {
  const path = `${clientId}/${year}-${String(month).padStart(2, '0')}.pdf`;
  const { error: upErr } = await supabase.storage.from('reports').upload(path, file, { upsert: true, contentType: 'application/pdf' });
  if (upErr) throw upErr;
  const existing = await supabase.from('client_reports').select('id').eq('client_id', clientId).eq('period_year', year).eq('period_month', month).limit(1);
  if (existing.data && existing.data.length) {
    const { error } = await supabase.from('client_reports').update({ source: 'uploaded', file_path: path, updated_at: new Date().toISOString() }).eq('id', existing.data[0].id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('client_reports').insert({
      client_id: clientId, period_year: year, period_month: month,
      title: `Rapport — ${MONTHS_FR[month - 1]} ${year}`, status: 'draft', source: 'uploaded', file_path: path,
      kpis: {}, sections: [], created_by: (await supabase.auth.getUser()).data.user?.id ?? null,
    });
    if (error) throw error;
  }
}

/** URL signée (1h) pour télécharger le PDF uploadé. */
export async function getReportPdfUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from('reports').createSignedUrl(path, 3600);
  if (error) return null;
  return data?.signedUrl ?? null;
}

// ── IA : analyse marché (edge function generate-report-narrative) ──
// Schéma calqué sur le reporting Refiner : issues d'appel, objections+contournement,
// concurrents+contexte, signaux hors-RDV, personas, recommandations.
export interface ReportNarrative {
  analyse?: string;
  issuesAppel?: { issue: string; nb?: number; pct?: string; detail?: string }[];
  objections?: { objection: string; freq?: string; exemples?: string; contournement?: string }[];
  concurrents?: { prestataire: string; entreprises?: string[]; contexte?: string }[];
  signaux?: { prospect?: string; entreprise?: string; signal?: string; action?: string }[];
  personasReceptifs?: string[];
  recommandations?: string[];
}

/** Narratif IA pour un rapport mensuel (CSM). */
export async function generateReportNarrative(reportId: string): Promise<ReportNarrative> {
  const { data, error } = await supabase.functions.invoke('generate-report-narrative', { body: { report_id: reportId } });
  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || 'Échec de la génération IA');
  return (data.narrative || {}) as ReportNarrative;
}

/** Analyse IA du marché d'un client sur une plage → mise en cache (client_analyses). */
export async function analyzeClientMarket(clientId: string, fromISO: string, toISO: string, rangeKey: string): Promise<{ narrative: ReportNarrative; generated_at: string }> {
  const { data, error } = await supabase.functions.invoke('generate-report-narrative', { body: { client_id: clientId, from: fromISO, to: toISO, range_key: rangeKey } });
  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || 'Échec de la génération IA');
  return { narrative: (data.narrative || {}) as ReportNarrative, generated_at: data.generated_at || new Date().toISOString() };
}

/** Analyse IA en cache (si déjà générée) pour un client × plage. */
export async function getCachedAnalysis(clientId: string, rangeKey: string): Promise<{ narrative: ReportNarrative; generated_at: string } | null> {
  const { data, error } = await supabase.from('client_analyses').select('narrative,generated_at').eq('client_id', clientId).eq('range_key', rangeKey).limit(1);
  if (error) throw error;
  return data && data.length ? { narrative: data[0].narrative as ReportNarrative, generated_at: data[0].generated_at as string } : null;
}

/** Narratif IA → blocs affichables (tableaux Refiner). */
export function narrativeToBlocks(n: ReportNarrative): ReportBlock[] {
  const b: ReportBlock[] = [];
  if (n.analyse) b.push({ id: 'ia_analyse', type: 'text', title: 'Analyse', body: n.analyse });
  if (n.issuesAppel?.length) {
    b.push({ id: 'ia_issues_chart', type: 'chart', chart: 'pie', title: "Répartition des issues d'appel", data: n.issuesAppel.map(i => ({ label: i.issue, value: Number(i.nb) || 0 })) });
    b.push({ id: 'ia_issues', type: 'table', title: "Détail des issues d'appel", columns: ['Issue', 'Nb', '%', 'Détail'], rows: n.issuesAppel.map(i => [i.issue, String(i.nb ?? ''), i.pct ?? '', i.detail ?? '']) });
  }
  if (n.objections?.length) b.push({ id: 'ia_obj', type: 'table', title: 'Objections & pistes de contournement', columns: ['Objection', 'Fréq.', 'Exemples', 'Piste'], rows: n.objections.map(o => [o.objection, o.freq ?? '', o.exemples ?? '', o.contournement ?? '']) });
  if (n.concurrents?.length) b.push({ id: 'ia_conc', type: 'table', title: 'Outils concurrents détectés', columns: ['Prestataire', 'Entreprises', 'Contexte'], rows: n.concurrents.map(c => [c.prestataire, (c.entreprises || []).join(', '), c.contexte ?? '']) });
  if (n.signaux?.length) b.push({ id: 'ia_sig', type: 'table', title: "Signaux d'intérêt (hors RDV)", columns: ['Prospect', 'Entreprise', 'Signal', 'Action'], rows: n.signaux.map(s => [s.prospect ?? '', s.entreprise ?? '', s.signal ?? '', s.action ?? '']) });
  if (n.personasReceptifs?.length) b.push({ id: 'ia_pers', type: 'bullets', title: 'Personas les plus réceptifs', items: n.personasReceptifs });
  if (n.recommandations?.length) b.push({ id: 'ia_reco', type: 'bullets', title: 'Recommandations', items: n.recommandations });
  return b;
}

/** Fusionne le narratif IA dans les blocs d'un rapport CSM (analyse, reco, objections, personas, concurrents). */
export function applyNarrative(sections: ReportBlock[], n: ReportNarrative): ReportBlock[] {
  const out: ReportBlock[] = sections.map(b => ({ ...b }));
  const upsertText = (id: string, title: string, body: string) => {
    const i = out.findIndex(b => b.id === id);
    if (i >= 0 && out[i].type === 'text') (out[i] as any).body = body;
    else out.push({ id, type: 'text', title, body });
  };
  const upsertBullets = (id: string, title: string, items: string[]) => {
    const i = out.findIndex(b => b.id === id);
    if (i >= 0 && out[i].type === 'bullets') (out[i] as any).items = items;
    else out.push({ id, type: 'bullets', title, items });
  };
  if (n.analyse) upsertText('analyse', 'Analyse', n.analyse);
  if (n.recommandations?.length) upsertText('reco', 'Recommandations', n.recommandations.map((r, i) => `${i + 1}. ${r}`).join('\n'));
  if (n.objections?.length) upsertBullets('objections', 'Objections rencontrées', n.objections.map(o => `${o.objection}${o.contournement ? ` — ${o.contournement}` : ''}`));
  if (n.personasReceptifs?.length) upsertBullets('personas', 'Personas les plus réceptifs', n.personasReceptifs);
  if (n.concurrents?.length) {
    const rows = n.concurrents.map(c => [c.prestataire, (c.entreprises || []).join(', ')]);
    const i = out.findIndex(b => b.id === 'concurrents');
    if (i >= 0 && out[i].type === 'table') (out[i] as any).rows = rows;
    else out.push({ id: 'concurrents', type: 'table', title: 'Concurrents identifiés', columns: ['Prestataire', 'Entreprises citées'], rows });
  }
  return out;
}

/** Publie / dépublie un rapport (visible au client seulement si publié). */
export async function setReportPublished(id: string, publish: boolean): Promise<void> {
  const { error } = await supabase.from('client_reports').update({
    status: publish ? 'published' : 'draft',
    published_at: publish ? new Date().toISOString() : null,
    published_by: publish ? (await supabase.auth.getUser()).data.user?.id ?? null : null,
  }).eq('id', id);
  if (error) throw error;
}

export const reportMonthLabel = (y: number, m: number) => `${MONTHS_FR[m - 1]} ${y}`;

// ── Santé clients (vue CSM / direction) ─────────────────────────────────
// Ligne brute de la table `clients` (colonnes utiles à la santé).
interface ClientRow {
  id: string;
  name: string | null;
  statut: string | null;          // 'actif' = client actif (source : clients.statut)
  contract_type: string | null;   // 'fixed' | 'fixed_plus_variable' | 'days'
  monthly_price: number | null;
  start_date: string | null;
  end_date: string | null;
}

export type HealthScore = 'ok' | 'attention' | 'risque';

export interface ClientHealth {
  id: string;
  name: string;
  contractType: ContractType | null;
  active: boolean;
  monthlyPrice: number | null;
  startDate: string | null;
  endDate: string | null;
  score: HealthScore;
  flags: string[];   // motifs binaires expliquant le score (jamais de texte en dur ailleurs)
  totalRdv?: number; honores?: number; qualifies?: number; noShow?: number;
  production?: 'ok' | 'stale' | 'none';   // 30 j glissants (relatif au snapshot)
  daysToEnd?: number | null;
  alerts?: string[];  // 2×2 delivery / facturation (spec-statut-client)
}

/**
 * Score santé SIMPLE (règles binaires, v1 — cf. design doc). Aucun algo :
 *  - client inactif OU contrat expiré → « risque »
 *  - données de contrat manquantes / échéance proche → « attention »
 *  - sinon → « ok ».
 * Fonction pure : testable et réutilisée par getClientsHealth().
 */
export function computeClientHealth(row: ClientRow, today = new Date()): ClientHealth {
  const active = row.statut === 'actif';
  const contractType = contractTypeFromRaw(row.contract_type);
  const end = row.end_date ? new Date(row.end_date) : null;
  const flags: string[] = [];

  if (!active) flags.push('Client inactif');
  const expired = end != null && end.getTime() < today.getTime();
  if (expired) flags.push('Contrat expiré');
  // Échéance dans les 30 jours (et pas déjà expiré).
  if (end != null && !expired) {
    const days = (end.getTime() - today.getTime()) / 86_400_000;
    if (days <= 30) flags.push('Échéance < 30 jours');
  }
  if (!contractType) flags.push('Type de contrat manquant');
  if (row.monthly_price == null || row.monthly_price <= 0) flags.push('Prix mensuel manquant');

  const score: HealthScore = (!active || expired) ? 'risque' : (flags.length > 0 ? 'attention' : 'ok');

  return {
    id: row.id,
    name: row.name || 'Client sans nom',
    contractType,
    active,
    monthlyPrice: row.monthly_price,
    startDate: row.start_date,
    endDate: row.end_date,
    score,
    flags,
  };
}

/** Santé de tous les clients (vue CSM/direction) : agrégats RDV réels + production 30 j +
 *  alertes delivery/facturation (2×2 spec-statut-client). Lecture seule. */
export async function getClientsHealth(): Promise<ClientHealth[]> {
  const { data: cs, error } = await supabase.from('clients')
    .select('id,name,statut,contract_type,monthly_price,price_per_day,days_per_month,start_date,end_date,onboarding_status')
    .order('name', { ascending: true });
  if (error) throw error;
  const rows = (cs ?? []) as any[];
  // Agrégats CÔTÉ SQL (rpc_clients_health_agg) : l'ancien transfert de lignes brutes était plafonné
  // à 1000 par PostgREST → honorés/no-show sous-comptés de -92 % sur 30 j (audit 08/10).
  const [{ data: aggRows, error: eAgg }, { data: contracts }] = await Promise.all([
    supabase.rpc('rpc_clients_health_agg'),
    supabase.from('v_contrat_actif').select('client_id,type,monthly_price,days_per_month,price_per_day,start_date,end_date'),
  ]);
  if (eAgg) throw eAgg;
  // Source unique du contrat = v_contrat_actif ; l'inline de clients n'est plus qu'un fallback (corrige le cas Hint).
  const cmap = new Map((contracts ?? []).map((c: any) => [c.client_id, c]));
  for (const r of rows) {
    const cv = cmap.get(r.id);
    if (cv) { r.contract_type = cv.type; r.monthly_price = cv.monthly_price; r.days_per_month = cv.days_per_month; r.price_per_day = cv.price_per_day; r.start_date = cv.start_date; r.end_date = cv.end_date; }
  }
  const recentProd = new Set((aggRows ?? []).filter((a: any) => a.recent_calls).map((a: any) => a.client_id));
  const agg = new Map<string, { total: number; honores: number; qualifies: number; noShow: number }>();
  const last = new Map<string, string>();
  for (const a of (aggRows ?? []) as any[]) {
    agg.set(a.client_id, { total: Number(a.appt_total) || 0, honores: Number(a.honores) || 0, qualifies: Number(a.qualifies) || 0, noShow: Number(a.no_show) || 0 });
    if (a.last_sched) last.set(a.client_id, String(a.last_sched));
  }

  return rows.map(r => {
    const g = agg.get(r.id) || { total: 0, honores: 0, qualifies: 0, noShow: 0 };
    const lp = last.get(r.id) || null;
    // Production = appels réels des 30 j (recentProd) ; sinon 'stale' si activité passée (agenda), sinon 'none'.
    const production: 'ok' | 'stale' | 'none' = recentProd.has(r.id) ? 'ok' : (lp ? 'stale' : 'none');
    const statut = r.statut || 'actif';
    const hasContract = !!r.days_per_month && !!r.price_per_day;
    const noShowRate = (g.honores + g.noShow) > 0 ? g.noShow / (g.honores + g.noShow) : 0;
    const qualRate = g.honores > 0 ? g.qualifies / g.honores : 0;
    const daysToEnd = r.end_date ? Math.round((new Date(r.end_date + 'T00:00:00').getTime() - Date.now()) / 86400000) : null;

    const flags: string[] = [];
    if (g.honores + g.noShow >= 4 && noShowRate > 0.25) flags.push(`No-show ${Math.round(noShowRate * 100)}%`);
    if (g.honores >= 3 && qualRate < 0.4) flags.push(`Qualif ${Math.round(qualRate * 100)}%`);
    if (!hasContract && statut === 'actif') flags.push('Sans contrat valide');
    if (daysToEnd != null && daysToEnd >= 0 && daysToEnd <= 60) flags.push(`Renouvellement ${daysToEnd}j`);

    const alerts: string[] = [];
    if (statut === 'actif' && hasContract && production !== 'ok') alerts.push('DELIVERY : facturé, pas de production récente');
    if (production === 'ok' && (statut === 'termine' || !hasContract)) alerts.push('FACTURATION : production sans contrat actif');

    let score: HealthScore = 'ok';
    if (alerts.length > 0 || flags.filter(fg => !fg.startsWith('Renouvellement')).length >= 2) score = 'risque';
    else if (flags.length >= 1) score = 'attention';

    return {
      id: r.id, name: r.name || 'Client', contractType: contractTypeFromRaw(r.contract_type), active: statut === 'actif',
      monthlyPrice: r.monthly_price ?? (hasContract ? r.price_per_day * r.days_per_month : null),
      startDate: r.start_date ?? null, endDate: r.end_date ?? null, score, flags,
      totalRdv: g.total, honores: g.honores, qualifies: g.qualifies, noShow: g.noShow, production, daysToEnd, alerts,
    };
  });
}

/** RDV d'un SDR pour "Ma journée". Sans sdr_id → tous (utile pour super_admin en dev). */
export async function getSdrAppointments(sdrId?: string): Promise<RdvRow[]> {
  let q = supabase
    .from('appointments')
    .select('id,prospect_name,company_name,job_title,date,time,lifecycle_status,icp_conformant,source,client_id,clients(name),calibration:calibrations(pricing_model)')
    .order('date', { ascending: false })
    .limit(40);
  if (sdrId) q = q.eq('sdr_id', sdrId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as RdvRow[];
}

/** RDV des clients donnés (pour « RDV à statuer » scopé aux favoris du SDR). */
export async function getRdvForClients(clientIds: string[]): Promise<RdvRow[]> {
  if (!clientIds.length) return [];
  const { data, error } = await supabase
    .from('appointments')
    .select('id,prospect_name,company_name,job_title,phone,date,time,lifecycle_status,icp_conformant,source,client_id,clients(name),calibration:calibrations(pricing_model)')
    .in('client_id', clientIds)
    .order('date', { ascending: false })
    .limit(300);
  if (error) throw error;
  return (data ?? []) as RdvRow[];
}

// ── Portail client (lecture) : rattachement + KPIs figés + résumés de journée ──
// Scoping : profile_clients (profile_id = auth user_id) + fallback profiles.client_id.
// Tout est DÉTERMINISTE (comptages/regroupements) — aucune classification IA ici.

/** client_id(s) rattachés au user courant. Vide pour un interne sans mapping. */
export async function getMyClientIds(userId: string, fallbackClientId?: string | null): Promise<string[]> {
  const { data, error } = await supabase.from('profile_clients').select('client_id').eq('profile_id', userId);
  if (error) throw error;
  const ids = (data ?? []).map((r: any) => r.client_id).filter(Boolean) as string[];
  if (!ids.length && fallbackClientId) ids.push(fallbackClientId);
  return [...new Set(ids)];
}

/** Liste des clients pour le sélecteur.
 *  - sans `ids` : clients ACTIFS cette année (vue v_active_clients) — pour le QA interne.
 *  - avec `ids` : ces clients précis, quel que soit leur statut — un client voit toujours son espace. */
export async function getClientOptions(ids?: string[]): Promise<{ id: string; name: string }[]> {
  if (ids && ids.length) {
    const { data, error } = await supabase.from('clients').select('id,name').in('id', ids).order('name');
    if (error) throw error;
    return (data ?? []) as { id: string; name: string }[];
  }
  const { data, error } = await supabase.from('v_active_clients').select('id,name').order('name');
  if (error) throw error;
  return (data ?? []) as { id: string; name: string }[];
}

export interface PortalKpis {
  rdvTotal: number; rdvHeld: number; rdvQualified: number; rdvBillable: number; noShow: number; // registre RDV
  calls30: number; connected30: number; conversations30: number; rdv30: number;   // activité 30 j
}
export interface DaySummaryItem { company: string | null; contact: string | null; summary: string; }
// calls = lignes call_logs (appels connectés remontés) ; connected = durée > 0 (connectés) ;
// conversations = durée > 30s (définition « conversation » du cerveau, definitions.md §2) ; rdv = bookés.
export interface DaySummary { date: string; calls: number; connected: number; conversations: number; rdv: number; items: DaySummaryItem[]; mailSent?: number; liSent?: number; }
export interface PortalFunnel { booked: number; held: number; qualified: number; billable: number; noShow: number; cancelled: number }
export interface ClientPortalData {
  clientId: string;
  contractType: ContractType | null;  // pilote l'affichage (RDV à facturer seulement en hybride)
  billsPerRdv: boolean;               // true = hybride (facture au RDV qualifié)
  from: string; to: string;           // plage d'activité effective (YYYY-MM-DD)
  rdv: RdvRow[]; kpis: PortalKpis; days: DaySummary[];
  // Funnel EXACT sur la plage [from, to] : counts côté serveur, bornes appliquées, RDV futurs et
  // « No reply – VM/SMS » exclus (audit 08/10 : le funnel était calculé sur un tableau tronqué à 200).
  funnel: PortalFunnel;
}

/** Prochaines journées de prospection planifiées pour un client (portail — régression V1→V2 :
 *  « quand appelez-vous pour moi ? » avait disparu du portail, audit 08/10). RLS : le client ne
 *  lit que SON planning (policies client_read_own_schedule / Users can view prospecting schedule). */
export interface UpcomingDay { date: string; day_type: string | null }
export async function getClientUpcomingSchedule(clientId: string): Promise<UpcomingDay[]> {
  const today = new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase.from('prospecting_schedule')
    .select('date,day_type')
    .eq('client_id', clientId).gte('date', today)
    .order('date', { ascending: true }).limit(10);
  if (error) throw error;
  return (data ?? []) as UpcomingDay[];
}

/** Type de contrat du client (calibrage actif) — source fiable, contrairement à la
 *  jointure par RDV souvent vide. null = pas encore de calibrage. */
export async function getClientContractType(clientId: string): Promise<ContractType | null> {
  const { data, error } = await supabase
    .from('calibrations').select('pricing_model').eq('client_id', clientId).eq('is_active', true).limit(1);
  if (error) throw error;
  return contractTypeFromRaw(data?.[0]?.pricing_model);
}

/** Registre RDV léger d'un client (liste complète, sans les KPIs ni résumés). */
export async function getClientRegistre(clientId: string): Promise<(RdvRow & { created_at?: string })[]> {
  const { data, error } = await supabase
    .from('appointments')
    .select('id,prospect_name,company_name,job_title,date,time,created_at,lifecycle_status,icp_conformant,client_id,sdr_id,clients(name),calibration:calibrations(pricing_model),sdr:profiles!appointments_sdr_id_fkey(sales_name)')
    .eq('client_id', clientId)
    .order('date', { ascending: false })
    .limit(300);
  if (error) throw error;
  return (data ?? []) as (RdvRow & { created_at?: string })[];
}

// "Tenu" = le RDV a eu lieu (au-delà de confirmé), quel que soit le verdict de qualif.
const HELD_PLUS: LifecycleStatus[] = ['held', 'qualified', 'not_qualified', 'validated', 'disputed', 'billed'];
// "Qualifié" = tenu ET conforme au calibrage (ce que le client attend, quel que soit le contrat).
const QUALIFIED_PLUS: LifecycleStatus[] = ['qualified', 'validated', 'billed'];

/** Toute la donnée du portail d'UN client sur une plage d'activité (défaut 30 j) :
 *  registre RDV (complet) + KPIs d'activité + résumés de journée. */
export async function getClientPortal(clientId: string, fromISO?: string, toISO?: string): Promise<ClientPortalData> {
  const now = Date.now();
  const since30 = fromISO ? new Date(fromISO + 'T00:00:00').toISOString() : new Date(now - 30 * 86400000).toISOString();
  const until = toISO ? new Date(toISO + 'T23:59:59').toISOString() : new Date(now).toISOString();
  const since14 = since30; // résumés sur la même plage que les KPIs

  // Type de contrat — pilote l'affichage : "à facturer" seulement en hybride.
  // Source = client_contracts (lisible par le client via RLS) ; fallback calibrations (interne :
  // les clients n'ont PAS de policy de lecture sur calibrations, qui porte l'ICP sensible).
  const [{ data: ct }, { data: cal }] = await Promise.all([
    supabase.from('client_contracts').select('pricing_model').eq('client_id', clientId).eq('is_active', true).order('version', { ascending: false }).limit(1),
    supabase.from('calibrations').select('pricing_model').eq('client_id', clientId).eq('is_active', true).limit(1),
  ]);
  const contractType = contractTypeFromRaw(ct?.[0]?.pricing_model ?? cal?.[0]?.pricing_model);

  // Registre RDV (appointments du client — table réduite par client)
  const { data: appts, error: e1 } = await supabase
    .from('appointments')
    .select('id,prospect_name,company_name,job_title,date,time,created_at,lifecycle_status,icp_conformant,status,call_id,client_id,clients(name),calibration:calibrations(pricing_model)')
    .eq('client_id', clientId)
    .order('date', { ascending: false })
    .limit(200);
  if (e1) throw e1;
  const rdv = (appts ?? []) as RdvRow[];
  const rdvHeld = rdv.filter(r => r.lifecycle_status && HELD_PLUS.includes(r.lifecycle_status)).length;
  const rdvQualified = rdv.filter(r => r.lifecycle_status && QUALIFIED_PLUS.includes(r.lifecycle_status)).length;
  const rdvBillable = rdv.filter(r => isBillable(r.lifecycle_status ?? null)).length;
  const noShow = rdv.filter(r => r.lifecycle_status === 'no_show').length;

  // Activité 30 j : comptages seuls (pas de transfert de lignes). started_at souvent NULL → created_at.
  const cnt = (b: any) => b.then((r: any) => r.count ?? 0) as Promise<number>;
  const base = () => supabase.from('call_logs').select('*', { count: 'exact', head: true }).eq('client_id', clientId).gte('created_at', since30).lte('created_at', until);
  const [calls30, connected30, conversations30, rdv30] = await Promise.all([
    cnt(base()),
    // Connecté = décroché (status), PAS durée>0 : un voicemail a une durée (audit 08/10 : +85 % sinon)
    cnt(base().eq('status', 'connected')),
    cnt(base().eq('status', 'connected').gt('duration_seconds', 30)),   // conversation = connecté > 30s (dico §2)
    // RDV pris = appointments créés sur la période (source de vérité, pas un flag d'appel)
    cnt(supabase.from('appointments').select('*', { count: 'exact', head: true }).eq('client_id', clientId).gte('created_at', since30).lte('created_at', until)),
  ]);

  // Funnel EXACT côté serveur sur la plage (date du RDV ∈ [from, to]) — le calcul sur le tableau
  // tronqué à 200 lignes sous-comptait les gros clients (MAY : 200 affichés pour 525 réels) et
  // comptait les RDV futurs ; les 226 « No reply – VM/SMS » hérités de la V1 sont exclus (bookés fantômes).
  const fromDay = since30.slice(0, 10), toDay = until.slice(0, 10);
  const NO_REPLY = '("No reply – VM/SMS","no_reply")';
  const fbase = () => supabase.from('appointments').select('*', { count: 'exact', head: true })
    .eq('client_id', clientId).gte('date', fromDay).lte('date', toDay).not('status', 'in', NO_REPLY);
  const [fBooked, fHeld, fQualified, fBillable, fNoShow, fCancelled] = await Promise.all([
    cnt(fbase()),
    cnt(fbase().in('lifecycle_status', ['held', 'qualified', 'not_qualified', 'validated', 'disputed', 'billed'])),
    cnt(fbase().in('lifecycle_status', ['qualified', 'validated', 'billed'])),
    cnt(fbase().in('lifecycle_status', ['validated', 'billed'])),
    cnt(fbase().eq('lifecycle_status', 'no_show')),
    cnt(fbase().eq('lifecycle_status', 'cancelled')),   // annulés : affichés en détail du « booké » pour que le compte tombe juste (réunion 15/10)
  ]);

  // Résumés de journée sur la plage : COMPTEURS exacts côté SQL (rpc_calls_daily — l'ancien
  // transfert plafonné à 800 lignes faisait disparaître des journées entières, audit 08/10) ;
  // la requête de lignes ne sert plus qu'aux extraits de résumés (les plus récents).
  const [{ data: dailyAgg, error: eD }, { data: calls, error: e2 }] = await Promise.all([
    supabase.rpc('rpc_calls_daily', { p_client_id: clientId, p_from: since14, p_to: until }),
    supabase
      .from('call_logs')
      .select('created_at,call_summary,prospect_company,prospect_first_name,prospect_last_name')
      .eq('client_id', clientId)
      .gte('created_at', since14)
      .lte('created_at', until)
      .not('call_summary', 'is', null)
      .order('created_at', { ascending: false })
      .limit(800),
  ]);
  if (eD) throw eD;
  if (e2) throw e2;
  const byDay = new Map<string, DaySummary>();
  for (const r of (dailyAgg ?? []) as any[]) {
    const date = String(r.day);
    byDay.set(date, { date, calls: Number(r.calls) || 0, connected: Number(r.connected) || 0, conversations: Number(r.conversations) || 0, rdv: 0, items: [] });
  }
  for (const c of (calls ?? []) as any[]) {
    const date = (c.created_at || '').slice(0, 10);
    const d = date && byDay.get(date);
    if (!d) continue;
    const summary = (c.call_summary || '').trim();
    if (summary && d.items.length < 40) {
      d.items.push({
        company: c.prospect_company ?? null,
        contact: [c.prospect_first_name, c.prospect_last_name].filter(Boolean).join(' ') || null,
        summary,
      });
    }
  }
  // RDV pris par jour (appointments créés ce jour), fusionné dans les journées d'appels.
  for (const a of rdv as any[]) {
    const date = (a.created_at || '').slice(0, 10);
    const d = date && byDay.get(date);
    if (d) d.rdv++;
  }
  await mergeOutreachDaily(byDay, clientId, since14, until);  // mail/LinkedIn envoyés par jour
  const days = [...byDay.values()].sort((a, b) => b.date.localeCompare(a.date));

  return {
    clientId, contractType, billsPerRdv: billsPerRdv(contractType),
    from: since30.slice(0, 10), to: until.slice(0, 10),
    rdv, kpis: { rdvTotal: rdv.length, rdvHeld, rdvQualified, rdvBillable, noShow, calls30, connected30, conversations30, rdv30 }, days,
    funnel: { booked: fBooked, held: fHeld, qualified: fQualified, billable: fBillable, noShow: fNoShow, cancelled: fCancelled },
  };
}

/** Journées d'activité d'UN client sur une plage, dérivées des call_logs (source mesurée,
 *  automatique — indépendante du formulaire SDR déclaratif). Une entrée par jour avec appels,
 *  connectés, RDV bookés et les résumés d'appels du jour. Récentes d'abord. */

/** Merge les volumes outreach ENVOYÉS (outreach_stats_daily) par jour dans un byDay de DaySummary.
 *  Crée une entrée pour un jour mail/LinkedIn sans appel (GTM full-stack) afin qu'il apparaisse. */
async function mergeOutreachDaily(byDay: Map<string, DaySummary>, clientId: string, sinceISO: string, untilISO: string): Promise<void> {
  const { data } = await supabase.from('outreach_stats_daily')
    .select('date,channel,sent').eq('client_id', clientId)
    .gte('date', sinceISO.slice(0, 10)).lte('date', untilISO.slice(0, 10));
  for (const r of (data ?? []) as any[]) {
    const date = String(r.date);
    let d = byDay.get(date);
    if (!d) { d = { date, calls: 0, connected: 0, conversations: 0, rdv: 0, items: [] }; byDay.set(date, d); }
    if (r.channel === 'linkedin') d.liSent = (d.liSent ?? 0) + (Number(r.sent) || 0);
    else d.mailSent = (d.mailSent ?? 0) + (Number(r.sent) || 0);
  }
}

export async function getClientDays(clientId: string, fromISO: string, toISO: string): Promise<DaySummary[]> {
  const since = new Date(fromISO + 'T00:00:00').toISOString();
  const until = new Date(toISO + 'T23:59:59').toISOString();
  // Compteurs par jour côté SQL (cap PostgREST 1000 — audit 08/10) ; lignes = extraits de résumés.
  const [{ data: dailyAgg, error: eD }, { data: calls, error }] = await Promise.all([
    supabase.rpc('rpc_calls_daily', { p_client_id: clientId, p_from: since, p_to: until }),
    supabase
      .from('call_logs')
      .select('created_at,call_summary,prospect_company,prospect_first_name,prospect_last_name')
      .eq('client_id', clientId).gte('created_at', since).lte('created_at', until)
      .not('call_summary', 'is', null)
      .order('created_at', { ascending: false }).limit(3000),
  ]);
  if (eD) throw eD;
  if (error) throw error;
  const byDay = new Map<string, DaySummary>();
  for (const r of (dailyAgg ?? []) as any[]) {
    const date = String(r.day);
    byDay.set(date, { date, calls: Number(r.calls) || 0, connected: Number(r.connected) || 0, conversations: Number(r.conversations) || 0, rdv: 0, items: [] });
  }
  for (const c of (calls ?? []) as any[]) {
    const date = (c.created_at || '').slice(0, 10);
    const d = date && byDay.get(date);
    if (!d) continue;
    const s = (c.call_summary || '').trim();
    if (s && d.items.length < 80) d.items.push({
      company: c.prospect_company ?? null,
      contact: [c.prospect_first_name, c.prospect_last_name].filter(Boolean).join(' ') || null,
      summary: s,
    });
  }
  const { data: appts } = await supabase.from('appointments')
    .select('created_at').eq('client_id', clientId).gte('created_at', since).lte('created_at', until);
  for (const a of (appts ?? []) as any[]) { const d = byDay.get((a.created_at || '').slice(0, 10)); if (d) d.rdv++; }
  await mergeOutreachDaily(byDay, clientId, since, until);  // mail/LinkedIn envoyés par jour (GTM full-stack)
  return [...byDay.values()].sort((a, b) => b.date.localeCompare(a.date));
}

// ─── SDR — « Ma journée » & « Mes clients » ─────────────────────────────────────────
// Clés (vérifiées) : prospecting_schedule.sdr_id = profiles.user_id = call_logs.sdr_profile_id.
// L'échange (conversation) = call_logs.duration_seconds > 30 (cerveau §2). Objectifs :
// cold call 40 échanges/jour ; GTM 12 échanges/jour/client.

export type SdrType = 'cold_call' | 'gtm' | 'mixte' | 'support' | '?';
export interface SdrOption { id: string; name: string; type: SdrType }
/** SDR RÉCEMMENT ACTIFS (calls dans les 60 j précédant le dernier call du snapshot → écarte
 *  ceux qui ont quitté la boîte), avec leur type déduit du channel des clients appelés :
 *  gtm seul = GTM, cold_call seul = Cold call, les deux = Mixte. */
// Activité d'appels par SDR (Minari → call_logs), sur une plage. created_at car started_at parfois null.
// NB : call_logs ne contient que les appels connectés + voicemails (pas les numéros sans réponse).
// « appels » = lignes loggées (connect + VM), « connectMin » = temps de connect total (minutes).
export interface SdrActivity { appels: number; conversations: number; connectes: number; voicemails: number; connectMin: number }
const ckey = (s: string) => s.toLowerCase().trim();
export interface SdrActivityResult {
  byId: Record<string, SdrActivity>;                 // par SDR
  byClient: Record<string, SdrActivity>;             // par `${sdrId}|${clientName minuscule}` → ventilation GTM
  teamById: Record<string, string | null>;           // prospecting_team par SDR
  missionsById: Record<string, number>;              // nb missions (client×jour) planifiées sur la période, par SDR
}
export async function getSdrCallActivity(fromISO: string, toISO: string): Promise<SdrActivityResult> {
  // Agrégation CÔTÉ SQL (rpc_sdr_activity) : PostgREST plafonne toute réponse à 1000 lignes —
  // l'ancien transfert de lignes brutes sous-comptait la vue semaine de -86 % (audit 08/10).
  const [{ data: acts, error: eA }, { data: profs }, { data: sched }] = await Promise.all([
    supabase.rpc('rpc_sdr_activity', { p_from: `${fromISO}T00:00:00`, p_to: `${toISO}T23:59:59` }),
    supabase.from('profiles').select('user_id, prospecting_team').not('prospecting_team', 'is', null),
    supabase.from('prospecting_schedule').select('sdr_id, date, client_id')
      .gte('date', fromISO).lte('date', toISO).not('client_id', 'is', null).limit(20000),
  ]);
  if (eA) throw eA;
  const byId: Record<string, SdrActivity> = {};
  const byClient: Record<string, SdrActivity> = {};
  for (const r of (acts ?? []) as any[]) {
    const a: SdrActivity = {
      appels: Number(r.appels) || 0, conversations: Number(r.conversations) || 0,
      connectes: Number(r.connectes) || 0, voicemails: Number(r.voicemails) || 0,
      connectMin: Number(r.connect_min) || 0,
    };
    if (r.lvl === 1) byId[r.sdr_profile_id] = a;
    else if (r.client_name) byClient[`${r.sdr_profile_id}|${ckey(String(r.client_name))}`] = a;
  }
  // Missions = nb de couples (client × jour) planifiés dans l'agenda sur la période (1 mission GTM = 1 client-jour).
  const missionSet = new Map<string, Set<string>>();
  for (const s of (sched ?? []) as any[]) {
    if (!s.sdr_id || !s.client_id) continue;
    if (!missionSet.has(s.sdr_id)) missionSet.set(s.sdr_id, new Set());
    missionSet.get(s.sdr_id)!.add(`${s.date}|${s.client_id}`);
  }
  const missionsById: Record<string, number> = {};
  for (const [k, v] of missionSet) missionsById[k] = v.size;
  const teamById: Record<string, string | null> = {};
  for (const p of (profs ?? []) as any[]) teamById[p.user_id] = p.prospecting_team;
  return { byId, byClient, teamById, missionsById };
}

// ─── Minari (API directe) — vrais COMPOSÉS / connectés / temps de connexion, par SDR ────
// Passe par l'Edge Function `minari-analytics` (la clé Minari reste côté serveur, dans le
// coffre). call_logs ne loggue que connectés+VM ; Minari donne TOUS les numéros composés.
// byId est keyé par sdr_profile_id (= profiles.user_id = appointments.sdr_id), mappé côté
// serveur via call_logs.agent_email. Retourne {} en cas d'échec (dégradation douce → on
// retombe sur call_logs dans l'UI).
export interface MinariActivity {
  composes: number;       // total_calls — tous les numéros composés (no-answer/busy/failed inclus)
  connectes: number;      // connected_calls
  conversations: number;  // connectés ≥ 30 s (= « échange »)
  talkMin: number;        // temps de parole cumulé (min)
  sessionMin: number;     // temps de dialing actif (min) = « temps de connexion »
  meetings: number;       // meetings_booked côté Minari
  connectRate: number | null;
  email: string | null;
  name: string | null;
}
export async function getMinariActivity(fromISO: string, toISO: string): Promise<Record<string, MinariActivity>> {
  try {
    const { data, error } = await supabase.functions.invoke('minari-analytics', {
      body: { start_date: fromISO, end_date: toISO },
    });
    if (error || !data || (data as any).error) return {};
    return ((data as any).byId ?? {}) as Record<string, MinariActivity>;
  } catch { return {}; }
}

export async function getSdrOptions(): Promise<SdrOption[]> {
  const { data: maxRow } = await supabase.from('call_logs').select('created_at').order('created_at', { ascending: false }).limit(1);
  const maxAt = maxRow?.[0]?.created_at ? new Date(maxRow[0].created_at as string) : new Date();
  const cutoff = new Date(maxAt.getTime() - 60 * 86400000).toISOString();
  const { data: rows } = await supabase.from('call_logs').select('sdr_profile_id,client_id').gte('created_at', cutoff).not('sdr_profile_id', 'is', null).limit(50000);
  const clientsBySdr = new Map<string, Set<string>>();
  for (const r of (rows ?? []) as any[]) {
    if (!clientsBySdr.has(r.sdr_profile_id)) clientsBySdr.set(r.sdr_profile_id, new Set());
    if (r.client_id) clientsBySdr.get(r.sdr_profile_id)!.add(r.client_id);
  }
  const sdrIds = [...clientsBySdr.keys()];
  if (!sdrIds.length) return [];
  const allClientIds = [...new Set([...clientsBySdr.values()].flatMap(s => [...s]))];
  const { data: cals } = allClientIds.length
    ? await supabase.from('calibrations').select('client_id,channel').in('client_id', allClientIds).eq('is_active', true)
    : { data: [] as any[] };
  const chan = new Map((cals ?? []).map((c: any) => [c.client_id, c.channel]));
  const { data: profs } = await supabase.from('profiles').select('user_id,sales_name,archived').in('user_id', sdrIds);
  const name = new Map((profs ?? []).map((p: any) => [p.user_id, p.sales_name || 'SDR']));
  const archived = new Set((profs ?? []).filter((p: any) => p.archived).map((p: any) => p.user_id));
  return sdrIds.filter(id => !archived.has(id)).map(id => {
    const chans = new Set([...clientsBySdr.get(id)!].map(c => chan.get(c)).filter(Boolean));
    const type: SdrType = chans.has('gtm') ? (chans.has('cold_call') ? 'mixte' : 'gtm') : (chans.has('cold_call') ? 'cold_call' : '?');
    return { id, name: (name.get(id) as string) || 'SDR', type };
  }).sort((a, b) => a.name.localeCompare(b.name));
}

// ─── Agenda d'affectation (manager) — SDR × client × jour ───────────────────────────────
export interface ScheduleCell { id: string; sdrId: string; clientId: string | null; clientName: string | null; date: string; dayType: string | null; workLocation: string | null }
export async function getWeekSchedule(fromISO: string, toISO: string): Promise<ScheduleCell[]> {
  const { data, error } = await supabase.from('prospecting_schedule')
    .select('id,sdr_id,client_id,date,day_type,work_location,clients(name)')
    .gte('date', fromISO).lte('date', toISO).limit(3000);
  if (error) throw error;
  return (data ?? []).map((r: any) => ({ id: r.id, sdrId: r.sdr_id, clientId: r.client_id, clientName: r.clients?.name ?? null, date: r.date, dayType: r.day_type, workLocation: r.work_location }));
}
/** Affecte un client à un SDR sur une ou plusieurs dates (GTM continu = toute la semaine). */
export async function assignScheduleDays(input: { sdrId: string; clientId: string | null; dates: string[]; dayType?: string; workLocation?: string }): Promise<void> {
  // month/year sont des colonnes GÉNÉRÉES (EXTRACT depuis date) → ne jamais les insérer.
  const rows = input.dates.map(date => ({
    sdr_id: input.sdrId, client_id: input.clientId, date, day_type: input.dayType || 'prospection',
    status: 'planned', work_location: input.workLocation || null,
  }));
  const { error } = await supabase.from('prospecting_schedule').insert(rows);
  if (error) throw error;
}
export async function removeScheduleDay(id: string): Promise<void> {
  const { error } = await supabase.from('prospecting_schedule').delete().eq('id', id);
  if (error) throw error;
}

export interface AssignableClient { id: string; name: string; channel: 'cold_call' | 'gtm' | null; sold: number | null; gtmMissions: number | null }
/** Clients affectables : ceux qui ont un calibrage actif (donc un canal), + jours vendus. */
export async function getAssignableClients(): Promise<AssignableClient[]> {
  const { data: cals } = await supabase.from('calibrations').select('client_id,channel').eq('is_active', true).not('client_id', 'is', null);
  const chan = new Map<string, string>();
  for (const c of (cals ?? []) as any[]) if (!chan.has(c.client_id)) chan.set(c.client_id, c.channel);
  const ids = [...chan.keys()];
  if (!ids.length) return [];
  // Jours vendus = somme des lignes de CONTRAT actives hors GTM (le legacy clients.days_per_month
  // vaut 4 pour tout le monde — fossile). Fallback legacy si aucun contrat ne porte de jours.
  const [{ data: cs }, { data: ccs }] = await Promise.all([
    supabase.from('clients').select('id,name,days_per_month,statut').in('id', ids),
    supabase.from('client_contracts').select('client_id,channel,days_per_month').eq('is_active', true).in('client_id', ids),
  ]);
  const soldByClient = new Map<string, number>();
  const gtmByClient = new Map<string, number>(); // lignes GTM : days_per_month = MISSIONS vendues
  for (const l of (ccs ?? []) as any[]) {
    if (!l.days_per_month) continue;
    if (l.channel === 'gtm') gtmByClient.set(l.client_id, (gtmByClient.get(l.client_id) || 0) + Number(l.days_per_month));
    else soldByClient.set(l.client_id, (soldByClient.get(l.client_id) || 0) + Number(l.days_per_month));
  }
  return (cs ?? [])
    .filter((c: any) => (c.statut ?? 'actif') === 'actif') // actifs seulement (pas en pause / terminés)
    .map((c: any) => ({
      id: c.id, name: c.name, channel: (chan.get(c.id) as any) ?? null,
      sold: soldByClient.get(c.id) ?? c.days_per_month ?? null,
      gtmMissions: gtmByClient.get(c.id) ?? null,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Clients sélectionnables pour une DEMANDE DE LISTE : TOUS les clients actifs (même sans
 *  calibrage), pour pouvoir demander une liste à un client neuf ou interne (ex. Hook lui-même).
 *  getAssignableClients (agenda) reste restreint aux clients calibrés. */
export async function getRequestableClients(): Promise<AssignableClient[]> {
  const { data } = await supabase.from('clients').select('id,name').eq('statut', 'actif').order('name');
  return (data ?? []).map((c: any) => ({ id: c.id, name: c.name, channel: null, sold: null, gtmMissions: null }));
}

// ── Échanges & calls d'un SDR (call_logs + résumés IA) ─────────────────────
export interface SdrCallRow {
  id: string; at: string; contact: string | null; company: string | null; jobTitle: string | null;
  duration: number; summary: string | null; clientId: string | null; clientName: string | null;
}
/** Tous les appels d'un SDR sur une plage, du plus récent au plus ancien, avec le résumé. */
export async function getSdrCalls(sdrId: string, fromISO: string, toISO: string): Promise<SdrCallRow[]> {
  const { data, error } = await supabase.from('call_logs')
    .select('id,created_at,duration_seconds,call_summary,prospect_first_name,prospect_last_name,prospect_company,prospect_job_title,client_id,clients(name)')
    .eq('sdr_profile_id', sdrId).gte('created_at', fromISO).lte('created_at', toISO)
    .order('created_at', { ascending: false }).limit(600);
  if (error) throw error;
  return (data ?? []).map((r: any) => ({
    id: r.id, at: r.created_at, duration: Number(r.duration_seconds) || 0,
    contact: [r.prospect_first_name, r.prospect_last_name].filter(Boolean).join(' ') || null,
    company: r.prospect_company ?? null, jobTitle: r.prospect_job_title ?? null,
    summary: (r.call_summary || '').trim() || null, clientId: r.client_id ?? null, clientName: r.clients?.name ?? null,
  }));
}

/** Réattribue un échange à un autre client (correction manuelle des Conversations).
 *  Met à jour client_id ET la colonne dénormalisée client_name pour rester cohérent. */
export async function reassignCallClient(callId: string, clientId: string, clientName: string): Promise<void> {
  const { error } = await supabase.from('call_logs')
    .update({ client_id: clientId, client_name: clientName })
    .eq('id', callId);
  if (error) throw error;
}

/** Clients pour lesquels le SDR était planifié ce jour-là — candidats suggérés à la réattribution
 *  (la bonne clé : l'appel appartient à un client que le SDR prospectait réellement ce jour). */
export async function getScheduledClientsForSdr(sdrId: string, dateISO: string): Promise<{ id: string; name: string }[]> {
  const { data, error } = await supabase.from('prospecting_schedule')
    .select('client_id, clients(name)')
    .eq('sdr_id', sdrId).eq('date', dateISO).not('client_id', 'is', null);
  if (error) throw error;
  const m = new Map<string, string>();
  for (const r of (data ?? []) as any[]) if (r.client_id) m.set(r.client_id, (r.clients as any)?.name ?? '—');
  return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

/** Liste des clients (sélecteur de réattribution manuelle). */
export async function getClientPickerOptions(): Promise<{ id: string; name: string }[]> {
  const { data, error } = await supabase.from('clients').select('id,name').order('name');
  if (error) throw error;
  return (data ?? []).map((r: any) => ({ id: r.id, name: r.name }));
}

// ICP essentiel d'un client (rappel de ciblage dans le détail d'un échange).
export interface ClientIcp { sectors: string[]; functions: string[]; exclusions: string[] }
export async function getClientIcp(clientId: string): Promise<ClientIcp> {
  const { data } = await supabase.from('calibrations').select('sectors,target_functions,exclusions').eq('client_id', clientId).eq('is_active', true).limit(1);
  const c: any = data?.[0];
  const arr = (v: any): string[] => Array.isArray(v) ? v.map(String).filter(Boolean) : [];
  return { sectors: arr(c?.sectors), functions: arr(c?.target_functions), exclusions: arr(c?.exclusions) };
}

// ── Évaluations d'appel (grille de scoring IA) ─────────────────────────────
export const EVAL_CRITERIA: { col: string; label: string }[] = [
  { col: 'score_opener', label: 'Accroche' },
  { col: 'score_permission', label: 'Permission' },
  { col: 'score_discovery_before_pitch', label: 'Découverte' },
  { col: 'score_pitch_value', label: 'Pitch / valeur' },
  { col: 'score_active_listening', label: 'Écoute active' },
  { col: 'score_objection_handling', label: 'Objections' },
  { col: 'score_deep_discovery', label: 'Découverte appro.' },
  { col: 'score_closing', label: 'Closing' },
  { col: 'score_tone', label: 'Ton' },
  { col: 'score_clean_exit', label: 'Sortie propre' },
];
export interface CallEval {
  callId: string; scoreGlobal: number | null; grade: string | null;
  criteria: { label: string; score: number | null }[];
  strengths: string[]; weaknesses: string[]; reco: string | null; followUp: string | null; rdvBooked: boolean;
}
/** Évaluations des appels d'un SDR sur une plage (grille 0-5 par critère + score global). */
export async function getSdrEvaluations(sdrId: string, fromISO: string, toISO: string): Promise<CallEval[]> {
  const cols = EVAL_CRITERIA.map(c => c.col).join(',');
  const { data, error } = await supabase.from('sdr_call_evaluations')
    .select(`call_log_id,score_global,grade,top_strengths,top_weaknesses,action_recommendation,verdict_follow_up,rdv_booked,${cols}`)
    .eq('sdr_profile_id', sdrId).gte('evaluated_at', fromISO).lte('evaluated_at', toISO).limit(1000);
  if (error) throw error;
  const arr = (v: any): string[] => Array.isArray(v) ? v.map(String).filter(Boolean) : [];
  return (data ?? []).map((r: any) => ({
    callId: r.call_log_id, scoreGlobal: r.score_global != null ? Number(r.score_global) : null, grade: r.grade ?? null,
    criteria: EVAL_CRITERIA.map(c => ({ label: c.label, score: r[c.col] != null ? Number(r[c.col]) : null })),
    strengths: arr(r.top_strengths), weaknesses: arr(r.top_weaknesses),
    reco: r.action_recommendation ?? null, followUp: r.verdict_follow_up ?? null, rdvBooked: !!r.rdv_booked,
  }));
}

// ── Favoris clients (accès rapide sidebar) ─────────────────────────────────
export async function getMyFavoriteIds(userId: string): Promise<string[]> {
  const { data } = await supabase.from('client_favorites').select('client_id').eq('user_id', userId);
  return (data ?? []).map((r: any) => r.client_id);
}
export async function getMyFavorites(userId: string): Promise<AssignedClient[]> {
  const ids = await getMyFavoriteIds(userId);
  if (!ids.length) return [];
  const [{ data: cs }, { data: cals }] = await Promise.all([
    supabase.from('clients').select('id,name').in('id', ids),
    supabase.from('calibrations').select('client_id,channel').in('client_id', ids).eq('is_active', true),
  ]);
  const chan = new Map<string, string>();
  for (const c of (cals ?? []) as any[]) if (!chan.has(c.client_id)) chan.set(c.client_id, c.channel);
  return (cs ?? []).map((c: any) => ({ id: c.id, name: c.name, channel: (chan.get(c.id) as any) ?? null, days: 0 })).sort((a, b) => a.name.localeCompare(b.name));
}
export async function toggleFavorite(userId: string, clientId: string, on: boolean): Promise<void> {
  if (on) await supabase.from('client_favorites').upsert({ user_id: userId, client_id: clientId }, { onConflict: 'user_id,client_id' });
  else await supabase.from('client_favorites').delete().eq('user_id', userId).eq('client_id', clientId);
}

// ── Dashboard de performance SDR (jour / semaine / mois, avec courbes) ──────
export interface SdrPerfDay { date: string; conversations: number; echanges: number; rdv: number; avgScore: number | null }
export interface SdrPerformance {
  totals: { conversations: number; echanges: number; rdv: number; honores: number; avgScore: number | null; nEval: number };
  byDay: SdrPerfDay[];
  criteria: { label: string; score: number }[];
  grades: { grade: string; n: number }[];
  improve: { label: string; count: number }[];
  recos: { text: string; count: number }[];
}
export async function getSdrPerformance(sdrId: string, fromISO: string, toISO: string): Promise<SdrPerformance> {
  const cols = EVAL_CRITERIA.map(c => c.col).join(',');
  const fromDate = fromISO.slice(0, 10), toDate = toISO.slice(0, 10);
  const [{ data: dailyCalls, error: eDC }, { data: evs }, { data: appts }] = await Promise.all([
    // Agrégats par jour côté SQL (cap PostgREST 1000 — audit 08/10). conv = connectés, ech = conversations (dico §2).
    supabase.rpc('rpc_sdr_calls_daily', { p_sdr: sdrId, p_from: fromISO, p_to: toISO }),
    supabase.from('sdr_call_evaluations').select(`evaluated_at,score_global,grade,top_weaknesses,action_recommendation,${cols}`).eq('sdr_profile_id', sdrId).gte('evaluated_at', fromISO).lte('evaluated_at', toISO).limit(8000),
    supabase.from('appointments').select('date,lifecycle_status').eq('sdr_id', sdrId).gte('date', fromDate).lte('date', toDate).limit(8000),
  ]);
  if (eDC) throw eDC;
  const dm = new Map<string, { conv: number; ech: number; rdv: number; sSum: number; sN: number }>();
  const bump = (k: string) => { let e = dm.get(k); if (!e) { e = { conv: 0, ech: 0, rdv: 0, sSum: 0, sN: 0 }; dm.set(k, e); } return e; };
  for (const r of (dailyCalls ?? []) as any[]) { const e = bump(String(r.day)); e.conv += Number(r.connected) || 0; e.ech += Number(r.conversations) || 0; }
  for (const a of (appts ?? []) as any[]) { if (a.date) bump(String(a.date).slice(0, 10)).rdv++; }
  for (const v of (evs ?? []) as any[]) { const e = bump(String(v.evaluated_at).slice(0, 10)); if (v.score_global != null) { e.sSum += Number(v.score_global); e.sN++; } }
  const byDay: SdrPerfDay[] = [...dm.entries()].sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, e]) => ({ date, conversations: e.conv, echanges: e.ech, rdv: e.rdv, avgScore: e.sN ? Math.round((e.sSum / e.sN) * 10) / 10 : null }));

  const nEval = (evs ?? []).length;
  const avg = (xs: number[]) => xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
  const criteria = EVAL_CRITERIA.map(c => ({ label: c.label, score: Math.round(avg((evs ?? []).map((v: any) => Number(v[c.col]) || 0)) * 10) / 10 }));
  const gradeMap: Record<string, number> = {}; const wk: Record<string, number> = {}; const rc: Record<string, number> = {};
  for (const v of (evs ?? []) as any[]) {
    if (v.grade) gradeMap[v.grade] = (gradeMap[v.grade] || 0) + 1;
    (Array.isArray(v.top_weaknesses) ? v.top_weaknesses : []).forEach((w: any) => { wk[String(w)] = (wk[String(w)] || 0) + 1; });
    if (v.action_recommendation) rc[v.action_recommendation] = (rc[v.action_recommendation] || 0) + 1;
  }
  const totalConv = (dailyCalls ?? []).reduce((s: number, r: any) => s + (Number(r.connected) || 0), 0);
  const totalEch = (dailyCalls ?? []).reduce((s: number, r: any) => s + (Number(r.conversations) || 0), 0);
  return {
    totals: {
      conversations: totalConv, echanges: totalEch, rdv: (appts ?? []).length,
      honores: (appts ?? []).filter((a: any) => a.lifecycle_status === 'held').length,
      avgScore: nEval ? Math.round(avg((evs ?? []).map((v: any) => Number(v.score_global) || 0)) * 10) / 10 : null, nEval,
    },
    byDay, criteria,
    grades: ['A', 'B', 'C', 'D'].map(g => ({ grade: g, n: gradeMap[g] || 0 })),
    improve: Object.entries(wk).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([label, count]) => ({ label, count })),
    recos: Object.entries(rc).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([text, count]) => ({ text, count })),
  };
}

// ── Stats d'équipe (leaderboard SDR : activité + conversion) ───────────────
export interface TeamStatRow {
  sdrId: string; name: string; type: SdrType;
  composes: number; connectes: number;
  conversations: number; echanges: number; echRate: number; days: number; perDay: number;
  bookes: number; honores: number; noShow: number; convRate: number; showUp: number;
}
/** Stats comparatives par SDR sur une plage : activité (conversations/échanges/cadence)
 * + conversion (RDV bookés/honorés, échange→RDV, show-up). Visible par toute l'équipe. */
export async function getTeamStats(fromISO: string, toISO: string): Promise<TeamStatRow[]> {
  const sdrs = await getAgendaSdrs();
  const ids = sdrs.map(s => s.id);
  if (!ids.length) return [];
  // Minari (API directe) = source EXACTE des composés/connectés/conversations (call_logs sous-capte
  // ~12%). Fallback call_logs via RPC d'agrégation serveur (le transfert de lignes brutes était
  // plafonné à 1000 par PostgREST → « stats fausses au mois », audit 08/10).
  const [{ data: acts, error: eA }, { data: apptStats, error: eB }, minari, { data: sched }] = await Promise.all([
    supabase.rpc('rpc_sdr_activity', { p_from: fromISO, p_to: toISO }),
    supabase.rpc('rpc_team_appt_stats', { p_from: fromISO, p_to: toISO }),
    getMinariActivity(fromISO, toISO),
    supabase.from('prospecting_schedule').select('sdr_id, date').gte('date', fromISO.slice(0, 10)).lte('date', toISO.slice(0, 10)).limit(20000),
  ]);
  if (eA) throw eA;
  if (eB) throw eB;
  const actById = new Map((acts ?? []).filter((r: any) => r.lvl === 1).map((r: any) => [r.sdr_profile_id, r]));
  const apptById = new Map((apptStats ?? []).map((r: any) => [r.sdr_id, r]));
  // « Jours actifs » pour la cadence (conn./jour) : MAX entre les jours avec appels loggués
  // (call_logs sous-capte → trop peu de jours distincts) et les jours planifiés à l'agenda.
  // Diviser un numérateur Minari par les seuls jours call_logs gonflait perDay (69/j affichés
  // pour ~30 réels — réunion 15/10). Le max protège des 2 trous (agenda incomplet OU call_logs creux).
  const schedDaysById = new Map<string, Set<string>>();
  for (const r of (sched ?? []) as any[]) {
    if (!r.sdr_id || !r.date) continue;
    if (!schedDaysById.has(r.sdr_id)) schedDaysById.set(r.sdr_id, new Set());
    schedDaysById.get(r.sdr_id)!.add(String(r.date));
  }
  // Les renforts/support (type 'support') sont exclus du classement et des agrégats d'équipe.
  return sdrs.filter(s => s.type !== 'support').map(s => {
    const cs: any = actById.get(s.id);
    const m = minari[s.id];
    const composes = m ? m.composes : Number(cs?.appels) || 0;
    const connectes = m ? m.connectes : Number(cs?.connectes) || 0;
    const echanges = m ? m.conversations : Number(cs?.conversations) || 0;
    const days = Math.max(Number(cs?.jours) || 0, schedDaysById.get(s.id)?.size ?? 0);
    const ap: any = apptById.get(s.id);
    const bookes = Number(ap?.bookes) || 0;
    const honores = Number(ap?.honores) || 0;
    const noShow = Number(ap?.no_show) || 0;
    return {
      sdrId: s.id, name: s.name, type: s.type,
      composes, connectes, conversations: connectes,
      echanges, echRate: connectes ? Math.round((echanges / connectes) * 100) : 0,
      days, perDay: days ? Math.round((connectes / days) * 10) / 10 : 0,
      bookes, honores, noShow,
      convRate: echanges ? Math.round((bookes / echanges) * 100) : 0,
      showUp: honores + noShow ? Math.round((honores / (honores + noShow)) * 100) : 0,
    };
  });
}

// ── Demandes de liste (SDR/CSM → sourcing) : file dans l'app, statuts ──────
// Circuit machine à listes (design 28/09, ~/docs/plans/2026-09-28-sourcing-plateforme-v2-design.md) :
// demandé → stratégie proposée (Gate 1 : un manager valide la stratégie) → stratégie validée
// → sourcing (agent de nuit) → liste à valider (Gate 2) → enrichissement+push → livré (label ML-*).
// Les anciens statuts (en_cours, a_valider, validee) restent lisibles pour les lignes historiques.
export type ListReqStatus = 'demande' | 'strategie_proposee' | 'strategie_validee' | 'sourcing' | 'en_cours' | 'a_valider' | 'validee' | 'livre' | 'rejetee';
export const LIST_REQ_STATUS: Record<ListReqStatus, string> = {
  demande: 'Demandé', strategie_proposee: 'Stratégie à valider', strategie_validee: 'Stratégie validée',
  sourcing: 'Sourcing', en_cours: 'En cours (sourcing)', a_valider: 'Liste à valider', validee: 'Validée', livre: 'Livré (uploadé)', rejetee: 'Rejetée',
};
export const LIST_REQ_FLOW: ListReqStatus[] = ['demande', 'strategie_proposee', 'strategie_validee', 'sourcing', 'a_valider', 'validee', 'livre'];
export interface ListRequest {
  id: string; requesterName: string | null; clientId: string | null; clientName: string | null;
  volume: number | null; deadline: string | null; channel: string | null;
  icpSectors: string | null; icpSizes: string | null; icpFunctions: string | null;
  status: ListReqStatus; note: string | null; createdAt: string;
}
function mapListReq(r: any): ListRequest {
  return {
    id: r.id, requesterName: r.requester_name ?? null, clientId: r.client_id ?? null, clientName: r.client_name ?? null,
    volume: r.volume ?? null, deadline: r.deadline ?? null, channel: r.channel ?? null,
    icpSectors: r.icp_sectors ?? null, icpSizes: r.icp_sizes ?? null, icpFunctions: r.icp_functions ?? null,
    status: (r.status ?? 'demande') as ListReqStatus, note: r.note ?? null, createdAt: r.created_at,
  };
}
export async function getListRequests(): Promise<ListRequest[]> {
  const { data, error } = await supabase.from('list_requests').select('*').order('created_at', { ascending: false }).limit(200);
  if (error) throw error;
  return (data ?? []).map(mapListReq);
}
export async function createListRequest(input: {
  requesterId: string; requesterName: string; clientId: string | null; clientName: string | null;
  volume: number | null; deadline: string | null; channel: string | null;
  icpSectors: string | null; icpSizes: string | null; icpFunctions: string | null; note: string | null;
}): Promise<void> {
  const { error } = await supabase.from('list_requests').insert({
    requester_id: input.requesterId, requester_name: input.requesterName, client_id: input.clientId, client_name: input.clientName,
    volume: input.volume, deadline: input.deadline, channel: input.channel,
    icp_sectors: input.icpSectors, icp_sizes: input.icpSizes, icp_functions: input.icpFunctions, note: input.note,
  });
  if (error) throw error;
}
export async function setListRequestStatus(id: string, status: ListReqStatus): Promise<void> {
  const { error } = await supabase.from('list_requests').update({ status, updated_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
}

// ── Machine à listes : stratégies (Gate 1) et listes stagées (Gate 2) ──────
export interface SourcingStrategy {
  id: string; requestId: string | null; clientName: string; status: 'proposee' | 'validee' | 'rejetee';
  rejectReason: string | null; analyseCalls: string | null; analyseMission: string | null;
  objectif: string | null; etatDesLieux: string | null; planSourcing: any;
  volumeCible: number | null; deadline: string | null; createdAt: string;
}
export async function getSourcingStrategies(): Promise<SourcingStrategy[]> {
  const { data, error } = await supabase.from('sourcing_strategies').select('*').order('created_at', { ascending: false }).limit(200);
  if (error) throw error;
  return (data ?? []).map((s: any) => ({
    id: s.id, requestId: s.request_id ?? null, clientName: s.client_name, status: s.status,
    rejectReason: s.reject_reason ?? null, analyseCalls: s.analyse_calls ?? null, analyseMission: s.analyse_mission ?? null,
    objectif: s.objectif ?? null, etatDesLieux: s.etat_des_lieux ?? null, planSourcing: s.plan_sourcing ?? null,
    volumeCible: s.volume_cible ?? null, deadline: s.deadline ?? null, createdAt: s.created_at,
  }));
}
/** Gate 1 : valider ou rejeter une stratégie. Le rejet renvoie la demande en « demandé » (la machine re-propose en tenant compte du motif). */
export async function decideStrategy(s: SourcingStrategy, decision: 'validee' | 'rejetee', by: string, motif?: string): Promise<void> {
  const patch: any = { status: decision, updated_at: new Date().toISOString() };
  if (decision === 'validee') { patch.validated_at = new Date().toISOString(); patch.validated_by = by; }
  else patch.reject_reason = motif || null;
  const { error } = await supabase.from('sourcing_strategies').update(patch).eq('id', s.id);
  if (error) throw error;
  if (s.requestId) await setListRequestStatus(s.requestId, decision === 'validee' ? 'strategie_validee' : 'demande');
}

export interface StagedListInfo {
  id: string; requestId: string | null; strategyId: string | null; clientName: string;
  status: string; pipedriveLabel: string | null; counts: any; report: any; createdAt: string; contactCount: number;
  digestNotes: string | null;
}
export async function getStagedListsInfo(): Promise<StagedListInfo[]> {
  const { data, error } = await supabase.from('staged_lists')
    .select('id, request_id, strategy_id, client_name, status, pipedrive_label, counts, digest_notes, job, created_at, staged_contacts(count)')
    .order('created_at', { ascending: false }).limit(100);
  if (error) throw error;
  return (data ?? []).map((l: any) => ({
    id: l.id, requestId: l.request_id ?? null, strategyId: l.strategy_id ?? null, clientName: l.client_name,
    status: l.status, pipedriveLabel: l.pipedrive_label ?? null, counts: l.counts ?? null,
    report: l.job?.report ?? null, createdAt: l.created_at,
    contactCount: l.staged_contacts?.[0]?.count ?? 0,
    digestNotes: l.digest_notes ?? null,
  }));
}
export interface StagedContactSample { id: string; firstname: string; lastname: string; jobTitle: string | null; company: string; city: string | null; signal: string | null; score: number | null; linkedin: string | null; }
/** Tous les contacts d'une liste stagée (Gate 2 : sélection individuelle). */
export async function getStagedContacts(listId: string, n = 400): Promise<StagedContactSample[]> {
  const { data, error } = await supabase.from('staged_contacts')
    .select('id, firstname, lastname, job_title, company, city, signal, score, linkedin')
    .eq('staged_list_id', listId).eq('status', 'staged')
    .order('score', { ascending: false, nullsFirst: false }).limit(n);
  if (error) throw error;
  return (data ?? []).map((c: any) => ({ id: c.id, firstname: c.firstname, lastname: c.lastname, jobTitle: c.job_title ?? null, company: c.company, city: c.city ?? null, signal: c.signal ?? null, score: c.score ?? null, linkedin: c.linkedin ?? null }));
}
/** Gate 2 granulaire : écarte les contacts non retenus (ils ne seront ni enrichis ni poussés). */
export async function rejectStagedContacts(ids: string[], motif: string): Promise<void> {
  if (!ids.length) return;
  const { error } = await supabase.from('staged_contacts')
    .update({ status: 'rejected', reject_reason: motif || 'écarté à la review (Gate 2)' })
    .in('id', ids);
  if (error) throw error;
}
/** Gate 2 : valider la liste (le worker enchaîne enrich FullEnrich → push Pipedrive) ou la rejeter avec motif. */
export async function decideStagedList(l: StagedListInfo, decision: 'validated' | 'rejected', motif?: string): Promise<void> {
  const patch: any = { status: decision };
  const { data, error } = await supabase.from('staged_lists').select('digest_notes').eq('id', l.id).single();
  if (!error && decision === 'rejected') patch.digest_notes = ((data?.digest_notes || '') + ' | REJET front ' + new Date().toISOString().slice(0, 10) + ' : ' + (motif || 'sans motif')).trim();
  const { error: e2 } = await supabase.from('staged_lists').update(patch).eq('id', l.id);
  if (e2) throw e2;
  if (l.requestId) await setListRequestStatus(l.requestId, decision === 'validated' ? 'validee' : 'demande');
}

/** Dernier jour où le SDR a une conversation enregistrée (pour ouvrir sur un jour avec des données). */
export async function getSdrLastActivityDate(sdrId: string): Promise<string | null> {
  const { data } = await supabase.from('call_logs').select('created_at').eq('sdr_profile_id', sdrId).order('created_at', { ascending: false }).limit(1);
  const at = data?.[0]?.created_at;
  return at ? new Date(at).toISOString().slice(0, 10) : null;
}

// ── Clients affectés à un SDR (rail de raccourcis) ─────────────────────────
export interface AssignedClient { id: string; name: string; channel: 'cold_call' | 'gtm' | null; days: number }
/** Clients qu'un SDR prospecte (jours prospection planifiés), triés par charge. */
export async function getSdrAssignedClients(sdrId: string): Promise<AssignedClient[]> {
  const { data } = await supabase.from('prospecting_schedule').select('client_id').eq('sdr_id', sdrId).eq('day_type', 'prospection').not('client_id', 'is', null).limit(3000);
  const count = new Map<string, number>();
  for (const r of (data ?? []) as any[]) count.set(r.client_id, (count.get(r.client_id) || 0) + 1);
  const ids = [...count.keys()];
  if (!ids.length) return [];
  const [{ data: cs }, { data: cals }] = await Promise.all([
    supabase.from('clients').select('id,name').in('id', ids),
    supabase.from('calibrations').select('client_id,channel').in('client_id', ids).eq('is_active', true),
  ]);
  const chan = new Map<string, string>();
  for (const c of (cals ?? []) as any[]) if (!chan.has(c.client_id)) chan.set(c.client_id, c.channel);
  return (cs ?? []).map((c: any) => ({ id: c.id, name: c.name, channel: (chan.get(c.id) as any) ?? null, days: count.get(c.id) || 0 }))
    .sort((a, b) => b.days - a.days);
}

// ── Équipe / gestion des users (cold call vs GTM) ──────────────────────────
export type ProspectingTeam = 'cold_call' | 'gtm' | null;
export interface InternalUser { userId: string; name: string; role: string; team: ProspectingTeam; squad: string | null; active: boolean; archived: boolean }
/** Utilisateurs internes (admin/super_admin/sdr) avec leur équipe de prospection + activité récente. */
export async function getInternalUsers(): Promise<InternalUser[]> {
  const [{ data: profs }, active] = await Promise.all([
    supabase.from('profiles').select('user_id,sales_name,role,prospecting_team,archived,squads(name)').in('role', ['admin', 'super_admin', 'csm', 'sdr']),
    getSdrOptions(),
  ]);
  const activeIds = new Set(active.map(s => s.id));
  return (profs ?? []).map((p: any) => ({
    userId: p.user_id, name: p.sales_name || '—', role: p.role, team: p.prospecting_team ?? null,
    squad: p.squads?.name ?? null, active: activeIds.has(p.user_id), archived: !!p.archived,
  })).sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
}
export async function setUserTeam(userId: string, team: ProspectingTeam): Promise<void> {
  const { error } = await supabase.from('profiles').update({ prospecting_team: team }).eq('user_id', userId);
  if (error) throw error;
}
/** Marquer un interne comme parti (archivé) ou le réintégrer. Ne supprime rien. */
export async function setUserArchived(userId: string, archived: boolean): Promise<void> {
  const { error } = await supabase.from('profiles').update({ archived }).eq('user_id', userId);
  if (error) throw error;
}
/** Changer le rôle (les droits) d'un interne. Réservé à la Direction (super_admin) côté UI. */
export async function setUserRole(userId: string, role: string): Promise<void> {
  const { error } = await supabase.from('profiles').update({ role }).eq('user_id', userId);
  if (error) throw error;
}

// ── Users clients ↔ accès société (profile_clients = source unique) ──────────────
// La V2 lit les accès dans `profile_clients` (many-to-many). `profiles.client_id`
// (inline, legacy) est conservé en repère le temps de la migration. On n'écrit QUE
// dans profile_clients pour ne plus alimenter la double source.
export interface ClientUserRow {
  userId: string; email: string; name: string | null; phone: string | null;
  clientIds: string[];              // accès réels (profile_clients)
  inlineClientId: string | null;    // legacy profiles.client_id (repère migration)
}
export interface ClientOption { id: string; name: string }
export interface ClientUsersData { users: ClientUserRow[]; clients: ClientOption[] }

export async function getClientUsersData(): Promise<ClientUsersData> {
  const [{ data: profs, error: e1 }, { data: links }, { data: cls }] = await Promise.all([
    supabase.from('profiles').select('user_id,email,sales_name,phone,client_id').eq('role', 'client'),
    supabase.from('profile_clients').select('profile_id,client_id'),
    supabase.from('clients').select('id,name').order('name'),
  ]);
  if (e1) throw e1;
  const byUser = new Map<string, string[]>();
  for (const l of (links ?? []) as any[]) {
    if (!byUser.has(l.profile_id)) byUser.set(l.profile_id, []);
    byUser.get(l.profile_id)!.push(l.client_id);
  }
  const users = ((profs ?? []) as any[]).map(p => ({
    userId: p.user_id, email: p.email || '—', name: p.sales_name ?? null, phone: p.phone ?? null,
    clientIds: byUser.get(p.user_id) ?? [], inlineClientId: p.client_id ?? null,
  })).sort((a, b) => (a.email || '').localeCompare(b.email || ''));
  return { users, clients: ((cls ?? []) as any[]).map(c => ({ id: c.id, name: c.name })) };
}

/** Donne à un user client l'accès à un client (idempotent). ignoreDuplicates = ON CONFLICT
 *  DO NOTHING : pas d'UPDATE (les admins non-super n'ont pas la policy UPDATE sur cette table). */
export async function addClientAccess(userId: string, clientId: string): Promise<void> {
  const { error } = await supabase.from('profile_clients')
    .upsert({ profile_id: userId, client_id: clientId }, { onConflict: 'profile_id,client_id', ignoreDuplicates: true });
  if (error) throw error;
}
/** Retire l'accès d'un user client à un client. Ne supprime pas le compte. */
export async function removeClientAccess(userId: string, clientId: string): Promise<void> {
  const { error } = await supabase.from('profile_clients')
    .delete().eq('profile_id', userId).eq('client_id', clientId);
  if (error) throw error;
}
/** Effectif de prospection assigné (cold_call/gtm), hors archivés. Fallback = SDR récemment actifs
 *  (type déduit) tant que personne n'est assigné, pour que l'agenda ne soit pas vide avant paramétrage. */
export async function getAgendaSdrs(): Promise<SdrOption[]> {
  // Assignables dans l'agenda = les SDR (prospecting_team) ET les renforts/support (is_support).
  // Les supports ont prospecting_team NULL → ils restent exclus de TOUTES les stats équipe
  // (qui filtrent prospecting_team IS NOT NULL) ; seul l'agenda les inclut (demande Charles 15/10).
  const { data } = await supabase.from('profiles').select('user_id,sales_name,prospecting_team,is_support').or('prospecting_team.not.is.null,is_support.eq.true').eq('archived', false);
  if (data && data.length) return (data as any[]).map(p => ({ id: p.user_id, name: p.sales_name || 'SDR', type: (p.is_support ? 'support' : p.prospecting_team) as SdrType })).sort((a, b) => a.name.localeCompare(b.name));
  return getSdrOptions();
}

// ── Parcours des échanges avant un RDV (demande Charles + équipe 11/08) ───────────────────
// Tous les appels du MÊME prospect (deal Pipedrive ou téléphone), en chronologie : le client et
// l'équipe voient d'où vient le RDV et combien d'échanges l'ont précédé. Résout aussi le défaut
// « le transcript attaché est celui du dernier appel » : tous les appels restent accessibles.
export interface ThreadCall {
  id: string; created_at: string; duration_seconds: number | null; status: string | null;
  sdr_name: string | null; call_summary: string | null; isBookingCall: boolean;
}
export async function getProspectCallThread(input: {
  clientId?: string | null; phone?: string | null; dealId?: number | null; callId?: string | null;
}): Promise<ThreadCall[]> {
  const ors: string[] = [];
  const pk = String(input.phone ?? '').replace(/\D/g, '').slice(-9); // 9 derniers chiffres (formats +33/0 variés)
  if (input.dealId) ors.push(`pipedrive_deal_id.eq.${input.dealId}`);
  if (pk.length === 9) ors.push(`to_phone.ilike.*${pk}`);
  if (!ors.length && input.callId) ors.push(`id.eq.${input.callId}`);
  if (!ors.length) return [];
  let q = supabase.from('call_logs')
    .select('id,created_at,duration_seconds,status,sdr_name,call_summary')
    .or(ors.join(','))
    .order('created_at', { ascending: true })
    .limit(50);
  if (input.clientId) q = q.eq('client_id', input.clientId);
  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as any[]).map(c => ({ ...c, isBookingCall: !!input.callId && c.id === input.callId }));
}

// ── Hub Rendez-vous : compteurs des files (badges onglets + menu) ──────────────────────────
// « Le travail arrive au lieu d'être cherché » : la rafale de statuage du matin (1/3 des
// transitions à 9h, audit UX 09/10) a besoin de voir les files sans naviguer.
export interface RdvCounts { aStatuer: number; aConfirmer: number; aValider: number }
export async function getRdvCounts(): Promise<RdvCounts> {
  const today = new Date().toISOString().slice(0, 10);
  const cnt = (b: any) => b.then((r: any) => r.count ?? 0) as Promise<number>;
  const base = () => supabase.from('appointments').select('*', { count: 'exact', head: true });
  const [aStatuer, aConfirmer, aValider] = await Promise.all([
    // passés sans issue (posé/confirmé/reporté à date passée) : la dette du matin
    cnt(base().in('lifecycle_status', ['posed', 'confirmed', 'rescheduled']).lt('date', today)),
    // à confirmer : posés des 7 PROCHAINS jours (le process confirme la veille/le jour —
    // compter tous les posés à venir donnait « 150 » sans signification, review Charles 09/10)
    cnt(base().eq('lifecycle_status', 'posed').gte('date', today)
      .lte('date', new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10))),
    // qualifiés en attente de validation CSM (la boucle de facturation hybride)
    cnt(base().eq('lifecycle_status', 'qualified')),
  ]);
  return { aStatuer, aConfirmer, aValider };
}

/** RDV qualifiés en attente de validation (file CSM/direction — 1 seul validated en 30 j :
 *  la facturation hybride n'avait pas de boucle d'usage, audit UX 09/10). */
export async function getQualifiedToValidate(): Promise<RdvRow[]> {
  const { data, error } = await supabase
    .from('appointments')
    .select('id,prospect_name,company_name,job_title,date,time,created_at,lifecycle_status,icp_conformant,source,client_id,clients(name),calibration:calibrations(pricing_model),sdr:profiles!appointments_sdr_id_fkey(sales_name)')
    .eq('lifecycle_status', 'qualified')
    .order('date', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as unknown as RdvRow[];
}

// ── Demandes de suppression de RDV (décision 01/10 : le SDR demande, un admin valide) ──────
export interface DeletionRequest {
  id: string; appointment_id: string; requested_by: string; reason: string; created_at: string;
  prospect: string | null; rdvDate: string | null; clientName: string | null; requesterName: string | null;
}
/** Le SDR (ou tout interne) demande la suppression d'un RDV mal saisi. Jamais de hard delete. */
export async function requestAppointmentDeletion(appointmentId: string, reason: string): Promise<void> {
  const uid = (await supabase.auth.getUser()).data.user?.id;
  const { error } = await supabase.from('appointment_deletion_requests')
    .insert({ appointment_id: appointmentId, requested_by: uid, reason });
  if (error) throw error;
}
/** File des demandes en attente (admins). */
export async function getPendingDeletionRequests(): Promise<DeletionRequest[]> {
  const { data, error } = await supabase.from('appointment_deletion_requests')
    .select('id,appointment_id,requested_by,reason,created_at,appointment:appointments(prospect_name,date,clients(name))')
    .eq('status', 'pending').order('created_at', { ascending: true }).limit(100);
  if (error) throw error;
  const rows = (data ?? []) as any[];
  const ids = [...new Set(rows.map(r => r.requested_by).filter(Boolean))];
  const names = new Map<string, string>();
  if (ids.length) {
    const { data: profs } = await supabase.from('profiles').select('user_id,sales_name,email').in('user_id', ids);
    for (const pr of (profs ?? []) as any[]) names.set(pr.user_id, pr.sales_name || pr.email || '—');
  }
  return rows.map(r => ({
    id: r.id, appointment_id: r.appointment_id, requested_by: r.requested_by, reason: r.reason, created_at: r.created_at,
    prospect: r.appointment?.prospect_name ?? null, rdvDate: r.appointment?.date ?? null,
    clientName: r.appointment?.clients?.name ?? null, requesterName: names.get(r.requested_by) ?? null,
  }));
}
/** Décision admin : approuvée → le RDV passe 'cancelled' + journal (RPC, jamais de delete). */
export async function decideAppointmentDeletion(requestId: string, approve: boolean): Promise<void> {
  const { error } = await supabase.rpc('decide_appointment_deletion', { p_request_id: requestId, p_approve: approve });
  if (error) throw error;
}

// ── Demandes de télétravail (SDR → manager) ────────────────────────────────
export interface TtRequest { id: string; sdrId: string; sdrName: string; date: string; status: 'pending' | 'approved' | 'refused' }
/** Le SDR pose une demande de TT sur un jour (upsert : re-demande = repasse en pending). */
export async function requestTeletravail(sdrId: string, dateISO: string): Promise<void> {
  const { error } = await supabase.from('tt_requests')
    .upsert({ sdr_id: sdrId, date: dateISO, status: 'pending', decided_at: null }, { onConflict: 'sdr_id,date' });
  if (error) throw error;
}
/** Demandes de TT (les pending en tête) + nom du SDR. `mineOnly` filtre sur un SDR. */
export async function getTtRequests(mineOnly?: string): Promise<TtRequest[]> {
  let q = supabase.from('tt_requests').select('id,sdr_id,date,status').order('date', { ascending: true }).limit(300);
  if (mineOnly) q = q.eq('sdr_id', mineOnly);
  const { data } = await q;
  const rows = (data ?? []) as any[];
  const ids = [...new Set(rows.map(r => r.sdr_id))];
  const { data: profs } = ids.length ? await supabase.from('profiles').select('user_id,sales_name').in('user_id', ids) : { data: [] as any[] };
  const name = new Map((profs ?? []).map((p: any) => [p.user_id, p.sales_name || 'SDR']));
  return rows.map(r => ({ id: r.id, sdrId: r.sdr_id, sdrName: name.get(r.sdr_id) || 'SDR', date: r.date, status: r.status }))
    .sort((a, b) => Number(b.status === 'pending') - Number(a.status === 'pending') || a.date.localeCompare(b.date));
}
/** Le manager approuve/refuse. Sur approbation, marque le jour de prospection en télétravail. */
export async function decideTtRequest(id: string, status: 'approved' | 'refused'): Promise<void> {
  const { data, error } = await supabase.from('tt_requests').update({ status, decided_at: new Date().toISOString() }).eq('id', id).select('sdr_id,date').limit(1);
  if (error) throw error;
  const r = data?.[0] as any;
  if (status === 'approved' && r) {
    await supabase.from('prospecting_schedule').update({ work_location: 'remote' }).eq('sdr_id', r.sdr_id).eq('date', r.date).eq('day_type', 'prospection');
  }
}

// ── Planification de capacité (jours-SDR) ──────────────────────────────────
export function workingDaysInMonth(year: number, month: number): number {
  const n = new Date(year, month, 0).getDate();
  let c = 0;
  for (let d = 1; d <= n; d++) { const dow = new Date(year, month - 1, d).getDay(); if (dow >= 1 && dow <= 5) c++; }
  return c;
}
// Jours d'absence (retranchés de la capacité) — matcher robuste (accents/casse/typos legacy).
// Inclut l'encadrement : journée payée non vendable (digest Kevin) → sort du dénominateur de capacité.
const ABSENCE_KEYS = ['conge', 'malade', 'ferie', 'ecole', 'formation', 'jour off', 'jouroff', 'off', 'out', 'mariage', 'absence', 'vacances', 'encadrement', 'coaching'];
export function isAbsenceDayType(dt: string | null | undefined): boolean {
  if (!dt) return false;
  const n = dt.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  if (n === 'prospection') return false;
  return ABSENCE_KEYS.some(k => n === k || n.startsWith(k));
}
export interface SdrClientLoad { clientId: string; clientName: string; channel: 'cold_call' | 'gtm' | null; days: number }
export interface SdrCapacityRow { sdrId: string; name: string; type: SdrType; capacityDays: number; capacityIsDefault: boolean; absenceDays: number; clients: SdrClientLoad[]; assignedDays: number; missions: number }
export interface ClientStaffing { clientId: string; name: string; channel: 'cold_call' | 'gtm' | null; sold: number | null; gtmMissions: number | null; planned: number }
/** Capacité selon le cerveau (definitions.md §9.6-9.7, Kevin 04/09/2026) :
 *  le pack se compte en JOURNÉES, le GTM en MISSIONS (3/full stack), et les deux ne se
 *  convertissent jamais. Capacité globale = moyenne des deux taux pondérée par producteurs. */
export interface CapacityBoard {
  sdrs: SdrCapacityRow[];
  clients: ClientStaffing[];
  team: {
    pack: { producers: number; capacityDays: number; assignedDays: number; freeDays: number; rate: number };
    gtm: { producers: number; missions: number; missionCap: number; freeMissions: number; rate: number };
    globalRate: number;   // (nPack×tauxPack + nFS×tauxGTM) / (nPack+nFS) — jamais de conversion places↔jours
    workingDays: number;
  };
}
/** Tableau de capacité du mois, dérivé de prospecting_schedule (modèle legacy) : par SDR
 *  (capacité = jours ouvrés − absences, ou override ; jours prestés = jours prospection),
 *  staffing par client (vendu vs planifié), totaux équipe → jours-SDR encore vendables. */
export async function getCapacityBoard(year: number, month: number): Promise<CapacityBoard> {
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const to = `${year}-${String(month).padStart(2, '0')}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
  // Clients actifs = prospectés récemment (75 j) — pour ne pas noyer la vue avec les dormants.
  const activeCut = new Date(Date.now() - 75 * 86400000).toISOString().slice(0, 10);
  const [sdrOptsAll, assignable, { data: rawS }, { data: rawCap }, { data: rawActive }] = await Promise.all([
    getAgendaSdrs(),
    getAssignableClients(),
    supabase.from('prospecting_schedule').select('sdr_id,client_id,day_type').gte('date', from).lte('date', to).limit(8000),
    supabase.from('sdr_capacity').select('sdr_id,capacity_days').eq('year', year).eq('month', month),
    supabase.from('prospecting_schedule').select('client_id').eq('day_type', 'prospection').gte('date', activeCut).not('client_id', 'is', null).limit(8000),
  ]);
  const activeClientIds = new Set(((rawActive ?? []) as any[]).map(r => r.client_id));
  // Comptes de démo exclus du calcul de capacité (ils dupliquent des missions réelles).
  const sdrOpts = sdrOptsAll.filter(s => !/d[ée]mo/i.test(s.name));
  // SDR planifiés mais hors effectif assigné → à afficher pour inciter à leur donner une équipe.
  // On EXCLUT les archivés (partis) : ils ne doivent plus être comptés dans la capacité.
  const rosterIds = new Set(sdrOpts.map(s => s.id));
  const orphanIds = [...new Set(((rawS ?? []) as any[]).map(r => r.sdr_id).filter(id => id && !rosterIds.has(id)))];
  if (orphanIds.length) {
    const { data: op } = await supabase.from('profiles').select('user_id,sales_name,archived').in('user_id', orphanIds);
    const nameById = new Map((op ?? []).map((p: any) => [p.user_id, p.sales_name]));
    const archived = new Set((op ?? []).filter((p: any) => p.archived).map((p: any) => p.user_id));
    for (const id of orphanIds) if (!archived.has(id)) sdrOpts.push({ id, name: nameById.get(id) || 'SDR', type: '?' });
  }
  const clientById = new Map(assignable.map(c => [c.id, c]));
  const capOverride = new Map((rawCap ?? []).map((r: any) => [r.sdr_id, Number(r.capacity_days)]));
  const workDays = workingDaysInMonth(year, month);

  // Agrégats par SDR et par client à partir des lignes-jours.
  const prospBySdr = new Map<string, Map<string, number>>(); // sdr → client → nb jours prospection
  const absenceBySdr = new Map<string, number>();
  const plannedByClient = new Map<string, number>();
  for (const r of (rawS ?? []) as any[]) {
    const dt = (r.day_type ?? '').toString();
    if (isAbsenceDayType(dt)) { absenceBySdr.set(r.sdr_id, (absenceBySdr.get(r.sdr_id) || 0) + 1); continue; }
    if (dt !== 'prospection' || !r.client_id) continue;
    if (!prospBySdr.has(r.sdr_id)) prospBySdr.set(r.sdr_id, new Map());
    const m = prospBySdr.get(r.sdr_id)!;
    m.set(r.client_id, (m.get(r.client_id) || 0) + 1);
    plannedByClient.set(r.client_id, (plannedByClient.get(r.client_id) || 0) + 1);
  }

  const sdrs: SdrCapacityRow[] = sdrOpts.map(s => {
    const absenceDays = absenceBySdr.get(s.id) || 0;
    const clients: SdrClientLoad[] = [...(prospBySdr.get(s.id) ?? new Map()).entries()]
      .map(([cid, days]) => { const c = clientById.get(cid); return { clientId: cid, clientName: c?.name ?? '—', channel: c?.channel ?? null, days: days as number }; })
      .sort((a, b) => b.days - a.days);
    const capacityDays = capOverride.has(s.id) ? capOverride.get(s.id)! : Math.max(0, workDays - absenceDays);
    // §9.5 : les journées passées sur un client GTM ne comptent pas comme journées.
    const assignedDays = clients.filter(c => c.channel !== 'gtm').reduce((t, c) => t + c.days, 0);
    // Mission GTM = client suivi dans le mois (pas des lignes-jours).
    const missions = clients.filter(c => c.channel === 'gtm').length;
    return { sdrId: s.id, name: s.name, type: s.type, capacityDays, capacityIsDefault: !capOverride.has(s.id), absenceDays, clients, assignedDays, missions };
  }).sort((a, b) => (b.assignedDays + b.missions) - (a.assignedDays + a.missions));

  const clients: ClientStaffing[] = assignable
    .filter(c => activeClientIds.has(c.id) || (plannedByClient.get(c.id) || 0) > 0) // actifs uniquement
    .map(c => ({ clientId: c.id, name: c.name, channel: c.channel, sold: c.sold, gtmMissions: c.gtmMissions, planned: plannedByClient.get(c.id) || 0 }))
    // clients les plus « sous-staffés » (manque de jours) en tête
    .sort((a, b) => ((b.sold ?? 0) - b.planned) - ((a.sold ?? 0) - a.planned));

  // ── Capacité selon le cerveau (§9.6-9.7) : pack en jours, GTM en missions, jamais convertis.
  const MISSIONS_PER_FS = 3; // plafond en vigueur (passera à 4 quand Kevin normalise)
  const gtmSdrs = sdrs.filter(s => s.type === 'gtm');
  const packSdrs = sdrs.filter(s => s.type !== 'gtm'); // cold_call + mixte + '?' comptent en jours
  const packCapacity = packSdrs.reduce((t, s) => t + s.capacityDays, 0);
  const packAssigned = packSdrs.reduce((t, s) => t + s.assignedDays, 0);
  const packRate = packCapacity ? packAssigned / packCapacity : 0;
  const missions = gtmSdrs.reduce((t, s) => t + s.missions, 0);
  const missionCap = gtmSdrs.length * MISSIONS_PER_FS;
  const gtmRate = missionCap ? missions / missionCap : 0; // >1 autorisé et affiché (Kevin 04/09)
  const producers = packSdrs.length + gtmSdrs.length;
  const globalRate = producers ? (packSdrs.length * packRate + gtmSdrs.length * gtmRate) / producers : 0;
  return {
    sdrs, clients,
    team: {
      pack: { producers: packSdrs.length, capacityDays: packCapacity, assignedDays: packAssigned, freeDays: packCapacity - packAssigned, rate: packRate },
      gtm: { producers: gtmSdrs.length, missions, missionCap, freeMissions: missionCap - missions, rate: gtmRate },
      globalRate, workingDays: workDays,
    },
  };
}
export async function setSdrCapacity(sdrId: string, year: number, month: number, days: number): Promise<void> {
  const { error } = await supabase.from('sdr_capacity').upsert({ sdr_id: sdrId, year, month, capacity_days: days }, { onConflict: 'sdr_id,year,month' });
  if (error) throw error;
}

export interface CapacityRow { clientId: string; name: string; sold: number | null; planned: number }
/** Jours vendus (contrat) vs jours planifiés (agenda) par client sur un mois → remplissage. */
export async function getMonthCapacity(year: number, month: number): Promise<CapacityRow[]> {
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const to = `${year}-${String(month).padStart(2, '0')}-${String(new Date(year, month, 0).getDate()).padStart(2, '0')}`;
  const { data: sched } = await supabase.from('prospecting_schedule').select('client_id').gte('date', from).lte('date', to).not('client_id', 'is', null).eq('day_type', 'prospection').limit(4000);
  const planned = new Map<string, number>();
  for (const s of (sched ?? []) as any[]) planned.set(s.client_id, (planned.get(s.client_id) || 0) + 1);
  const ids = [...planned.keys()];
  if (!ids.length) return [];
  const { data: cs } = await supabase.from('clients').select('id,name,days_per_month').in('id', ids);
  return (cs ?? []).map((c: any) => ({ clientId: c.id, name: c.name, sold: c.days_per_month ?? null, planned: planned.get(c.id) || 0 }))
    .sort((a, b) => b.planned - a.planned);
}

// ─── Registre clients (hub interne : CSM / manager / SDR) ───────────────────────────────
// ── Données à compléter : clients actifs avec des champs manquants ─────────────────────
export interface ClientCompletion {
  id: string; name: string; channel: 'cold_call' | 'gtm' | null;
  contract_type: string | null; days_per_month: number | null; price_per_day: number | null;
  pricing_model: string | null; fixed_price: number | null; price_per_qualified_rdv: number | null;
  start_date: string | null; end_date: string | null;
  calibrationActive: boolean; calibrageDone: boolean;
  missing: string[]; total: number;
}
export async function getDataCompleteness(): Promise<ClientCompletion[]> {
  const { data: cs, error } = await supabase.from('clients')
    .select('id,name,contract_type,days_per_month,price_per_day,start_date,end_date,statut')
    .eq('statut', 'actif').order('name');
  if (error) throw error;
  const ids = (cs ?? []).map((c: any) => c.id);
  if (!ids.length) return [];
  const [{ data: cals }, { data: briefs }] = await Promise.all([
    supabase.from('calibrations').select('client_id,channel,pricing_model,fixed_price,price_per_qualified_rdv,is_active').in('client_id', ids).eq('is_active', true),
    supabase.from('calibration_briefs').select('client_id,done').in('client_id', ids),
  ]);
  const calById = new Map((cals ?? []).map((c: any) => [c.client_id, c]));
  const doneById = new Map((briefs ?? []).map((b: any) => [b.client_id, b.done]));
  return (cs ?? []).map((c: any) => {
    const cal: any = calById.get(c.id);
    const pm = cal?.pricing_model ?? null;
    const missing: string[] = [];
    if (!c.contract_type && !cal?.channel) missing.push('Offre');
    if (!pm) missing.push('Modèle de prix');
    else if (pm === 'days' && c.price_per_day == null) missing.push('Prix/jour');
    else if ((pm === 'fixed' || pm === 'gtm') && cal?.fixed_price == null) missing.push('Forfait');
    else if (pm === 'fixed_plus_variable' && (cal?.fixed_price == null || cal?.price_per_qualified_rdv == null)) missing.push('Prix hybride');
    if (c.days_per_month == null) missing.push('Jours/mois');
    if (!c.start_date) missing.push('Date début');
    if (!c.end_date) missing.push('Date fin');
    if (!cal) missing.push('Calibrage');
    return {
      id: c.id, name: c.name, channel: (cal?.channel as any) ?? null,
      contract_type: c.contract_type ?? null, days_per_month: c.days_per_month ?? null, price_per_day: c.price_per_day ?? null,
      pricing_model: pm, fixed_price: cal?.fixed_price ?? null, price_per_qualified_rdv: cal?.price_per_qualified_rdv ?? null,
      start_date: c.start_date ?? null, end_date: c.end_date ?? null,
      calibrationActive: !!cal, calibrageDone: !!doneById.get(c.id),
      missing, total: 6,
    };
  }).sort((a, b) => b.missing.length - a.missing.length || a.name.localeCompare(b.name));
}

export interface RegistryClient {
  id: string; name: string; company_name: string | null; statut: string | null; onboarding_status: string | null;
  contract_type: string | null; days_per_month: number | null; price_per_day: number | null;
  lastProspection: string | null; calibrageDone: boolean;
  channel: Channel | null; offers: Channel[]; plannedThisMonth: number; plannedNextMonth: number;
  rdvBooked: number; rdvHeld: number;                        // activité RDV (tout l'historique)
  mailCampaigns: number; mailReplies: number; liCampaigns: number; liReplies: number; // mail + LinkedIn
}
export const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
/** Liste de tous les clients + infos clés (contrat, statut, canal, jours planifiés mois courant/suivant). */
export async function getClientsRegistry(): Promise<RegistryClient[]> {
  const { data: cs, error } = await supabase.from('clients')
    .select('id,name,company_name,statut,onboarding_status,contract_type,days_per_month,price_per_day')
    .order('name', { ascending: true }).limit(600);
  if (error) throw error;
  const ids = (cs ?? []).map((c: any) => c.id);
  // Agrégats RDV + agenda CÔTÉ SQL (cap PostgREST 1000 : les transferts de lignes sous-comptaient — audit 08/10)
  const [{ data: schedAgg, error: eS }, { data: briefs }, { data: cals }, { data: contracts }, { data: apptAgg, error: eA2 }, outreachMap] = await Promise.all([
    supabase.rpc('rpc_sched_by_client'),
    ids.length ? supabase.from('calibration_briefs').select('client_id,done').in('client_id', ids) : Promise.resolve({ data: [] as any[] }),
    ids.length ? supabase.from('calibrations').select('client_id,channel').eq('is_active', true).in('client_id', ids) : Promise.resolve({ data: [] as any[] }),
    ids.length ? supabase.from('v_contrat_actif').select('client_id,type,days_per_month,price_per_day').in('client_id', ids) : Promise.resolve({ data: [] as any[] }),
    supabase.rpc('rpc_clients_health_agg'),
    fetchOutreachByClient(),
  ]);
  if (eS) throw eS;
  if (eA2) throw eA2;
  // RDV par client (booké = toute ligne posée ; honoré = HELD_PLUS, cf. dico §1 — même liste dans la RPC)
  const rdvB = new Map<string, number>(); const rdvH = new Map<string, number>();
  for (const a of (apptAgg ?? []) as any[]) {
    rdvB.set(a.client_id, Number(a.appt_total) || 0);
    rdvH.set(a.client_id, Number(a.honores) || 0);
  }
  const cmap = new Map((contracts ?? []).map((c: any) => [c.client_id, c])); // contrat = v_contrat_actif, inline en fallback
  const last = new Map<string, string>();
  const pThis = new Map<string, number>(); const pNext = new Map<string, number>();
  for (const s of (schedAgg ?? []) as any[]) {
    if (s.last_date) last.set(s.client_id, String(s.last_date));
    pThis.set(s.client_id, Number(s.planned_this) || 0);
    pNext.set(s.client_id, Number(s.planned_next) || 0);
  }
  const doneMap = new Map((briefs ?? []).map((b: any) => [b.client_id, b.done]));
  const chan = new Map<string, string>();                 // canal primaire (1er actif) — pilote le filtrage SDR
  const offers = new Map<string, Set<string>>();          // tous les canaux actifs (multi-offres)
  for (const c of (cals ?? []) as any[]) {
    if (!c.channel) continue;
    if (!chan.has(c.client_id)) chan.set(c.client_id, c.channel);
    if (!offers.has(c.client_id)) offers.set(c.client_id, new Set());
    offers.get(c.client_id)!.add(c.channel);
  }
  return (cs ?? []).map((c: any) => {
    const cv = cmap.get(c.id);
    const o = outreachMap.get(c.id);
    return {
      ...c,
      contract_type: cv?.type ?? c.contract_type,
      days_per_month: cv?.days_per_month ?? c.days_per_month,
      price_per_day: cv?.price_per_day ?? c.price_per_day,
      lastProspection: last.get(c.id) ?? null, calibrageDone: !!doneMap.get(c.id),
      channel: (chan.get(c.id) as any) ?? null, offers: [...(offers.get(c.id) ?? [])] as Channel[],
      plannedThisMonth: pThis.get(c.id) || 0, plannedNextMonth: pNext.get(c.id) || 0,
      rdvBooked: rdvB.get(c.id) || 0, rdvHeld: rdvH.get(c.id) || 0,
      mailCampaigns: o?.mailCampaigns || 0, mailReplies: o?.mailReplies || 0,
      liCampaigns: o?.liCampaigns || 0, liReplies: o?.liReplies || 0,
    };
  });
}

// Fichiers déposés sur la fiche client (bucket client-files, dossier = client_id).
export interface ClientFile { name: string; size: number; updatedAt: string | null }
export async function listClientFiles(clientId: string): Promise<ClientFile[]> {
  const { data, error } = await supabase.storage.from('client-files').list(clientId, { limit: 200, sortBy: { column: 'updated_at', order: 'desc' } });
  if (error) throw error;
  return (data ?? []).filter((f: any) => f.id).map((f: any) => ({ name: f.name, size: f.metadata?.size ?? 0, updatedAt: f.updated_at ?? null }));
}
export async function uploadClientFile(clientId: string, file: File): Promise<void> {
  const safe = file.name.replace(/[^\w.\-]+/g, '_');
  const { error } = await supabase.storage.from('client-files').upload(`${clientId}/${Date.now()}_${safe}`, file, { upsert: false });
  if (error) throw error;
}
export async function clientFileUrl(clientId: string, name: string): Promise<string | null> {
  const { data } = await supabase.storage.from('client-files').createSignedUrl(`${clientId}/${name}`, 3600);
  return data?.signedUrl ?? null;
}
export async function deleteClientFile(clientId: string, name: string): Promise<void> {
  const { error } = await supabase.storage.from('client-files').remove([`${clientId}/${name}`]);
  if (error) throw error;
}

export interface ClientCore {
  id: string; name: string; company_name: string | null; contact_name: string | null; email: string | null;
  phone: string | null; sector: string | null; website: string | null; statut: string | null;
  onboarding_status: string | null; contract_type: string | null; days_per_month: number | null;
  price_per_day: number | null; value_proposition: string | null; calibrage_date: string | null;
  start_date: string | null; end_date: string | null; signed_date: string | null; onboarding_validated_at: string | null;
  statut_reason: string | null; statut_reprise: string | null;
}
export async function getClientCore(id: string): Promise<ClientCore | null> {
  const { data, error } = await supabase.from('clients')
    .select('id,name,company_name,contact_name,email,phone,sector,website,statut,onboarding_status,contract_type,days_per_month,price_per_day,value_proposition,calibrage_date,start_date,end_date,signed_date,onboarding_validated_at,statut_reason,statut_reprise')
    .eq('id', id).limit(1);
  if (error) throw error;
  const core = (data?.[0] as ClientCore) ?? null;
  if (!core) return null;
  // Source unique du contrat = v_contrat_actif (inline en fallback).
  const { data: cv } = await supabase.from('v_contrat_actif')
    .select('type,days_per_month,price_per_day,start_date,end_date').eq('client_id', id).limit(1);
  if (cv?.[0]) {
    core.contract_type = cv[0].type ?? core.contract_type;
    core.days_per_month = cv[0].days_per_month ?? core.days_per_month;
    core.price_per_day = cv[0].price_per_day ?? core.price_per_day;
    core.start_date = cv[0].start_date ?? core.start_date;
    core.end_date = cv[0].end_date ?? core.end_date;
  }
  return core;
}

// Contrat vivant d'un client, lu depuis la calibration active (formule + canal + prix).
export interface ClientContract { pricingModel: ContractType | null; channel: string | null; fixedPrice: number | null; pricePerRdv: number | null }
export async function getClientContract(clientId: string): Promise<ClientContract | null> {
  const { data } = await supabase.from('calibrations')
    .select('pricing_model,channel,fixed_price,price_per_qualified_rdv,version')
    .eq('client_id', clientId).eq('is_active', true).order('version', { ascending: false }).limit(1);
  const c: any = data?.[0];
  if (!c) return null;
  return {
    pricingModel: contractTypeFromRaw(c.pricing_model), channel: c.channel ?? null,
    fixedPrice: c.fixed_price != null ? Number(c.fixed_price) : null,
    pricePerRdv: c.price_per_qualified_rdv != null ? Number(c.price_per_qualified_rdv) : null,
  };
}

// ─── Calibrage guidé — support d'entretien (analyse IA du site) ─────────────────────────
export interface CalibrationBriefSection { titre: string; questions: string[] }
export interface CalibrationBriefData {
  resume?: string; cible_probable?: string; personas?: string[];
  objections_probables?: string[]; concurrents_possibles?: string[];
  sections?: CalibrationBriefSection[]; gates_suggeres?: { libelle: string; type: string }[];
}
export interface CalibrationBriefRow {
  website: string | null; sector: string | null;
  brief: CalibrationBriefData | null; answers: Record<string, string>; visio_url: string | null;
  done?: boolean;
}

// Worklist des calibrages (pour le CSM) : clients en onboarding, avec leur date + statut fait.
export interface CalibrageItem { id: string; name: string; website: string | null; calibrage_date: string | null; done: boolean; onboarding_status: string | null }
export async function getCalibrageWorklist(): Promise<CalibrageItem[]> {
  const { data: cs, error } = await supabase.from('clients')
    .select('id,name,website,calibrage_date,onboarding_status').not('onboarding_status', 'is', null).limit(400);
  if (error) throw error;
  const ids = (cs ?? []).map((c: any) => c.id);
  const { data: briefs } = ids.length
    ? await supabase.from('calibration_briefs').select('client_id,done,website').in('client_id', ids)
    : { data: [] as any[] };
  const doneMap = new Map((briefs ?? []).map((b: any) => [b.client_id, b.done]));
  const webMap = new Map((briefs ?? []).map((b: any) => [b.client_id, b.website]));
  return (cs ?? []).map((c: any) => ({
    id: c.id, name: c.name, website: c.website || webMap.get(c.id) || null,
    calibrage_date: c.calibrage_date, done: !!doneMap.get(c.id), onboarding_status: c.onboarding_status,
  }));
}
export async function markCalibrageDone(clientId: string, done: boolean): Promise<void> {
  const { error } = await supabase.from('calibration_briefs')
    .upsert({ client_id: clientId, done, done_at: done ? new Date().toISOString() : null }, { onConflict: 'client_id' });
  if (error) throw error;
}

/** Analyse le site du client via l'IA → brief + questions orientées pour le calibrage. */
export async function generateCalibrationBrief(input: { client_name?: string; website: string; sector?: string; notes?: string }): Promise<{ brief: CalibrationBriefData; debug?: any }> {
  const { data, error } = await supabase.functions.invoke('generate-calibration-brief', { body: input });
  if (error) throw error;
  if (!data?.success) throw new Error((data?.error || 'Analyse indisponible') + (data?.debug ? ' · ' + JSON.stringify(data.debug) : ''));
  return { brief: (data.brief ?? {}) as CalibrationBriefData, debug: data.debug };
}

export async function getCalibrationBrief(clientId: string): Promise<CalibrationBriefRow | null> {
  const { data, error } = await supabase.from('calibration_briefs')
    .select('website,sector,brief,answers,visio_url').eq('client_id', clientId).limit(1);
  if (error) throw error;
  return (data?.[0] as CalibrationBriefRow) ?? null;
}

export async function saveCalibrationBrief(clientId: string, patch: Partial<CalibrationBriefRow>): Promise<void> {
  const { error } = await supabase.from('calibration_briefs')
    .upsert({ client_id: clientId, ...patch, updated_at: new Date().toISOString() }, { onConflict: 'client_id' });
  if (error) throw error;
}

// ─── Onboarding client (CSM, flagship) — de la signature au lancement (cerveau) ────────
export interface OnboardingClient {
  id: string; name: string; company_name: string | null; onboarding_status: string | null;
  contact_name: string | null; email: string | null; value_proposition: string | null;
  days_per_month: number | null; price_per_day: number | null; contract_type: string | null;
  calibrage_date: string | null;
  calibrationSigned: boolean; hasContract: boolean;
}

/** Liste des clients avec l'état de leur onboarding (calibration signée ? contrat en base ?). */
export async function getOnboardingClients(): Promise<OnboardingClient[]> {
  const { data: cs, error } = await supabase.from('clients')
    .select('id,name,company_name,onboarding_status,contact_name,email,value_proposition,days_per_month,price_per_day,contract_type,calibrage_date')
    .order('created_at', { ascending: false }).limit(500);
  if (error) throw error;
  const ids = (cs ?? []).map((c: any) => c.id);
  const [{ data: cals }, { data: cts }] = await Promise.all([
    ids.length ? supabase.from('calibrations').select('client_id,client_signed_at').eq('is_active', true).in('client_id', ids) : Promise.resolve({ data: [] as any[] }),
    ids.length ? supabase.from('client_contracts').select('client_id').eq('is_active', true).in('client_id', ids) : Promise.resolve({ data: [] as any[] }),
  ]);
  const signed = new Set((cals ?? []).filter((c: any) => c.client_signed_at).map((c: any) => c.client_id));
  const withCt = new Set((cts ?? []).map((c: any) => c.client_id));
  return (cs ?? []).map((c: any) => ({
    ...c,
    calibrationSigned: signed.has(c.id),
    hasContract: withCt.has(c.id) || (!!c.days_per_month && !!c.price_per_day),
  }));
}

/** Crée un client (onboarding démarré). Réutilise le schéma clients (statut actif). */
export async function createOnboardingClient(input: { name: string; company_name?: string; email?: string; phone?: string; contact_name?: string; sector?: string }): Promise<string> {
  const { data, error } = await supabase.from('clients').insert([{
    name: input.name.trim(),
    company_name: (input.company_name || input.name).trim(),
    email: input.email?.trim().toLowerCase() || null,
    phone: input.phone?.trim() || null,
    contact_name: input.contact_name?.trim() || null,
    sector: input.sector?.trim() || null,
    statut: 'actif', onboarding_status: 'pending',
  }]).select('id').maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Création du client échouée');
  return data.id as string;
}

// Enregistre le contrat d'un client : PRIX/MOIS partout (le prix/jour est dérivé si des jours
// sont fournis). Écrit client_contracts (legacy/registre) ET la calibration active (source V2 :
// pricing_model + fixe + prix/RDV). GTM/mailing = mensuel sans jours ; hybride = fixe + prix/RDV.
export async function saveClientContract(clientId: string, input: {
  pricing: 'fixed' | 'fixed_plus_variable' | 'gtm'; type: string; channel: string;
  monthly: number; days?: number | null; pricePerRdv?: number | null;
  startDate?: string | null; months?: number | null;   // début + durée (en mois) → end_date calculée
}): Promise<void> {
  // UNE écriture transactionnelle côté base (save_client_contract, tout-ou-rien) : fini
  // l'enchaînement de 4 écritures sans vérification qui a produit la dérive mesurée par l'audit
  // 07-08/10 (25 types / 26 prix divergents). clients.* inline n'est PLUS écrit : client_contracts
  // est LA vérité ; calibrations reste un simple cache dérivé (syncPrimaryContract).
  const { error } = await supabase.rpc('save_client_contract', {
    p_client_id: clientId,
    p_type: input.type,
    p_channel: input.channel,
    p_pricing_model: input.pricing,
    p_monthly: input.monthly,
    p_days: input.days && input.days > 0 ? input.days : null,
    p_price_per_rdv: input.pricing === 'fixed_plus_variable' ? (input.pricePerRdv ?? null) : null,
    p_start_date: input.startDate || null,
    p_months: input.months && input.months > 0 ? input.months : null,
  });
  if (error) throw error;
  await syncPrimaryContract(clientId);  // cache « contrat principal » (calibrations), dérivé des lignes
  await syncBpFromContracts(clientId);  // le BP reflète le nouveau contrat (Muzzo ne se reproduira plus)
}

export async function updateClientFields(clientId: string, patch: Record<string, any>): Promise<void> {
  const { error } = await supabase.from('clients').update(patch).eq('id', clientId);
  if (error) throw error;
}

/** Écrit le contrat via la RPC transactionnelle. clients.* inline n'est plus écrit (audit 08/10). */
export async function upsertClientContract(clientId: string, c: { price_per_day: number; days_per_month: number; billing_unit: string; type: string }): Promise<void> {
  const { error } = await supabase.rpc('save_client_contract', {
    p_client_id: clientId, p_type: c.type, p_channel: c.type,
    p_pricing_model: 'fixed', p_monthly: c.price_per_day * c.days_per_month,
    p_days: c.days_per_month, p_price_per_rdv: null, p_start_date: null, p_months: null,
  });
  if (error) throw error;
  await syncPrimaryContract(clientId);
  await syncBpFromContracts(clientId);
}

export async function setOnboardingStatus(clientId: string, status: string): Promise<void> {
  await updateClientFields(clientId, { onboarding_status: status, ...(status === 'validated' ? { onboarding_validated_at: new Date().toISOString() } : {}) });
}

/** Invite le compte client (edge function invite-user), avec le token de session. */
export async function inviteClientUser(email: string, clientId: string, firstName?: string, lastName?: string, phone?: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session?.access_token ?? ''}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role: 'client', client_id: clientId, first_name: firstName ?? null, last_name: lastName ?? null, phone: phone ?? null }),
  });
  const result = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(result.error || "Échec de l'invitation");
}

/** Invite un interne (SDR, CSM, Interne) : même fonction serveur que les clients, sans société.
 *  Renvoie l'id du compte créé (null si le compte existait déjà). */
export async function inviteInternalUser(email: string, role: 'sdr' | 'csm' | 'admin', firstName?: string, lastName?: string): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-user`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session?.access_token ?? ''}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role, client_id: null, first_name: firstName ?? null, last_name: lastName ?? null, phone: null }),
  });
  const result = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(result.error || "Échec de l'invitation");
  return (result.user_id as string) ?? null;
}

/** Checklist du déroulé (étapes cochées) d'un SDR pour une date. Persistée → vérifiable manager. */
export async function getSdrChecklist(sdrId: string, dateISO: string): Promise<number[]> {
  const { data, error } = await supabase.from('sdr_day_checklist').select('done_steps').eq('sdr_id', sdrId).eq('date', dateISO).limit(1);
  if (error) throw error;
  return (data?.[0]?.done_steps as number[]) ?? [];
}
export async function saveSdrChecklist(sdrId: string, dateISO: string, mode: string, doneSteps: number[]): Promise<void> {
  const { error } = await supabase.from('sdr_day_checklist')
    .upsert({ sdr_id: sdrId, date: dateISO, mode, done_steps: doneSteps }, { onConflict: 'sdr_id,date' });
  if (error) throw error;
}

export interface SdrClient { id: string; name: string; channel: string | null }
/** Clients affectés à un SDR (distincts, depuis le planning) + leur channel de calibrage. */
export async function getSdrClients(sdrId: string): Promise<SdrClient[]> {
  const { data: sched } = await supabase.from('prospecting_schedule')
    .select('client_id,clients(name)').eq('sdr_id', sdrId).not('client_id', 'is', null).limit(4000);
  const nameById = new Map<string, string>();
  for (const r of (sched ?? []) as any[]) if (r.client_id) nameById.set(r.client_id, r.clients?.name || 'Client');
  const ids = [...nameById.keys()];
  if (!ids.length) return [];
  const { data: cals } = await supabase.from('calibrations').select('client_id,channel').in('client_id', ids).eq('is_active', true);
  const chanById = new Map((cals ?? []).map((c: any) => [c.client_id, c.channel]));
  return ids.map(id => ({ id, name: nameById.get(id)!, channel: (chanById.get(id) as string) ?? null }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface CalibrationBrief { cible: string[]; secteurs: string[]; zones: string[]; exclusions: string[] }
export interface SdrDayClient {
  clientId: string; clientName: string; channel: string | null;
  dayType: string | null; workLocation: string | null;
  planned: boolean; // client planifié ce jour (mission) — sinon : confirmation/dépannage, hors objectifs (réunion GTM 10/09)
  echanges: number; echanges2min: number; connected: number; calls: number; bookes: number; emails: number; items: DaySummaryItem[];
  brief: CalibrationBrief | null; // conseils du jour : ICP essentiel du calibrage
}
// Normalise un champ (array | string JSON | texte) en liste de chaînes.
const toArr = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map(String).filter(Boolean);
  if (typeof v === 'string' && v.trim()) { try { const p = JSON.parse(v); if (Array.isArray(p)) return p.map(String); } catch { /* */ } return [v.trim()]; }
  return [];
};
export interface SdrFeedItem { at: string; company: string | null; contact: string | null; summary: string; duration: number }
export interface SdrDay {
  date: string; mode: 'cold_call' | 'gtm';
  missions: string[];            // day_type planifiés (texte agenda, ex. « GTM Avenria », « prospection »)
  clients: SdrDayClient[]; feed: SdrFeedItem[];
  echangesTotal: number; connectedTotal: number; bookesTotal: number;
}

/** Activité d'UN SDR sur UNE date (mesurée depuis call_logs + appointments), par client,
 *  fusionnée avec le planning du jour. Sert de cockpit « Ma journée » (live via realtime). */
export async function getSdrDay(sdrId: string, dateISO: string): Promise<SdrDay> {
  const since = new Date(dateISO + 'T00:00:00').toISOString();
  const until = new Date(dateISO + 'T23:59:59').toISOString();

  const [{ data: sched }, { data: calls }, { data: appts }] = await Promise.all([
    supabase.from('prospecting_schedule').select('client_id,clients(name),day_type,work_location').eq('sdr_id', sdrId).eq('date', dateISO),
    supabase.from('call_logs').select('client_id,duration_seconds,status,call_summary,prospect_company,prospect_first_name,prospect_last_name,created_at')
      .eq('sdr_profile_id', sdrId).gte('created_at', since).lte('created_at', until).order('created_at', { ascending: false }).limit(1000),
    supabase.from('appointments').select('client_id,created_at').eq('sdr_id', sdrId).gte('created_at', since).lte('created_at', until),
  ]);

  const byClient = new Map<string, SdrDayClient>();
  const ensure = (id: string, name?: string) => {
    let c = byClient.get(id);
    if (!c) { c = { clientId: id, clientName: name || 'Client', channel: null, dayType: null, workLocation: null, planned: false, echanges: 0, echanges2min: 0, connected: 0, calls: 0, bookes: 0, emails: 0, items: [], brief: null }; byClient.set(id, c); }
    else if (name && c.clientName === 'Client') c.clientName = name;
    return c;
  };
  // L'agenda a souvent client_id NULL avec le client planqué dans day_type (« GTM Avenria »).
  // On n'invente donc pas de client depuis l'agenda : les clients réels viennent des call_logs.
  // On garde les day_type comme « missions planifiées » du jour.
  const missionSet = new Set<string>();
  for (const s of (sched ?? []) as any[]) {
    if (s.day_type) missionSet.add(s.day_type);
    if (s.client_id) { const c = ensure(s.client_id, s.clients?.name); c.dayType = s.day_type ?? c.dayType; c.workLocation = s.work_location ?? c.workLocation; }
  }

  const feed: SdrFeedItem[] = [];
  for (const cl of (calls ?? []) as any[]) {
    if (!cl.client_id) continue;
    const c = ensure(cl.client_id);
    const dur = cl.duration_seconds ?? 0;
    // « connecté » = décroché (status), pas durée>0 (un voicemail/sonnerie a une durée). Échange = connecté > 30 s.
    const isConnected = cl.status === 'connected';
    c.calls++; if (isConnected) c.connected++; if (isConnected && dur > 30) c.echanges++; if (isConnected && dur > 120) c.echanges2min++;
    const s = (cl.call_summary || '').trim();
    const contact = [cl.prospect_first_name, cl.prospect_last_name].filter(Boolean).join(' ') || null;
    if (s && c.items.length < 60) c.items.push({ company: cl.prospect_company ?? null, contact, summary: s });
    if (s) feed.push({ at: cl.created_at, company: cl.prospect_company ?? null, contact, summary: s, duration: dur });
  }
  for (const a of (appts ?? []) as any[]) { if (a.client_id) ensure(a.client_id).bookes++; }

  // Mails envoyés (déclaratif prospecting_days) — GTM surtout ; souvent 0 tant qu'Instantly n'est pas connecté.
  const { data: pdays } = await supabase.from('prospecting_days')
    .select('client_id,emails_sent').eq('sdr_id', sdrId).or(`date.eq.${dateISO},prospection_date.eq.${dateISO}`);
  for (const p of (pdays ?? []) as any[]) { if (p.client_id) { const e = byClient.get(p.client_id); if (e) e.emails += (p.emails_sent || 0); } }

  const ids = [...byClient.keys()];
  if (ids.length) {
    // Noms des clients (ceux dérivés des call_logs n'en ont pas encore).
    const { data: cli } = await supabase.from('clients').select('id,name').in('id', ids);
    for (const c of (cli ?? []) as any[]) { const e = byClient.get(c.id); if (e && c.name) e.clientName = c.name; }
    // Calibrage actif — channel (cold call vs GTM) + brief « conseils du jour ».
    const { data: cals } = await supabase.from('calibrations')
      .select('client_id,channel,target_functions,sectors,zones,exclusions').in('client_id', ids).eq('is_active', true);
    for (const c of (cals ?? []) as any[]) {
      const e = byClient.get(c.client_id); if (!e) continue;
      e.channel = c.channel ?? null;
      e.brief = { cible: toArr(c.target_functions), secteurs: toArr(c.sectors), zones: toArr(c.zones), exclusions: toArr(c.exclusions) };
    }
  }

  // Missions du jour vs hors-mission (réunion GTM 10/09, Kevin) : un appel de CONFIRMATION sur un
  // client non planifié (ex. Etixu chez Quentin) ne compte pas dans les objectifs d'échanges.
  // planned = client présent dans l'agenda du jour ; si l'agenda est vide, tout compte (repli).
  const plannedIds = new Set(((sched ?? []) as any[]).filter(s => s.client_id).map(s => s.client_id));
  for (const c of byClient.values()) c.planned = plannedIds.size === 0 || plannedIds.has(c.clientId);

  const clients = [...byClient.values()].sort((a, b) =>
    Number(b.planned) - Number(a.planned) || b.echanges - a.echanges || a.clientName.localeCompare(b.clientName));
  const mode: 'cold_call' | 'gtm' = clients.some(c => c.channel === 'gtm') ? 'gtm' : 'cold_call';
  const missionsOnly = clients.filter(c => c.planned);
  return {
    date: dateISO, mode, missions: [...missionSet], clients, feed,
    echangesTotal: missionsOnly.reduce((s, c) => s + c.echanges, 0),   // objectifs = missions du jour seulement
    connectedTotal: missionsOnly.reduce((s, c) => s + c.connected, 0),
    bookesTotal: clients.reduce((s, c) => s + c.bookes, 0),            // un booké compte toujours, mission ou pas
  };
}

// ─── (Legacy) Journées de prospection déclaratives (formulaire SDR) — remplacé par getClientDays ─
export interface ProspectingDay {
  id: string;
  date: string;                 // prospection_date sinon date
  sdrName: string | null;
  calls: number; conversations: number; emails: number; rdvBooked: number;
  gptSummary: string | null;    // résumé IA propre (affiché au client)
  sessionSummary: string | null;// résumé rédigé par le SDR
  objections: string | null; marketFeedback: string | null; leadsQuality: string | null;
}

/** Journées de prospection visibles par le client (résumés de fin de journée), récentes d'abord.
 *  Univers call_logs / prospecting_days (déclaratif SDR + résumé GPT), pas le nouveau système. */
export async function getClientProspectingDays(clientId: string): Promise<ProspectingDay[]> {
  const { data, error } = await supabase
    .from('prospecting_days')
    .select('id,date,prospection_date,sdr_first_name,calls_count,conversations_count,emails_sent,rdvs_booked,common_objections,market_feedback,leads_quality_feedback,session_summary,gpt_summary,is_visible_by_client')
    .eq('client_id', clientId)
    .eq('is_visible_by_client', true)
    .limit(400);
  if (error) throw error;
  return ((data ?? []) as any[])
    .map(r => ({
      id: r.id,
      date: (r.prospection_date || r.date) as string,
      sdrName: r.sdr_first_name ?? null,
      calls: r.calls_count ?? 0, conversations: r.conversations_count ?? 0,
      emails: r.emails_sent ?? 0, rdvBooked: r.rdvs_booked ?? 0,
      gptSummary: r.gpt_summary ?? null, sessionSummary: r.session_summary ?? null,
      objections: r.common_objections ?? null, marketFeedback: r.market_feedback ?? null,
      leadsQuality: r.leads_quality_feedback ?? null,
    }))
    .filter(d => !!d.date)
    .sort((a, b) => b.date.localeCompare(a.date));
}
